import { useMemo } from 'react';
import { GST_TXN_TYPE, GST_SUB_TXN_TYPE } from '../constants';
import Swal from 'sweetalert2';
import moment from 'moment';
import html2canvas from 'html2canvas';
// import libxl from LibXL
// import ExcelJS from 'exceljs';

import * as XLSX from 'xlsx-js-style';
import { GSTR_EXCEL_DOWNLOAD_START } from '../redux/types';
import axiosInstance from 'axios';
import { GSTR_EXCEL_DOWNLOAD_SUCCESS } from '../redux/types';
import { toast } from 'react-toastify';
import { GSTR_EXCEL_DOWNLOAD_ERROR } from '../redux/types';
import { getAdjustedDocs } from '../redux/actions/salesInvoice';
import { useGlobal } from '../contexts';
import { getDaybook } from '../redux/actions/daybook';
import { getItem } from '../redux/actions/item';
import { getAccount } from '../redux/actions/account';

export const validateGST = {
   // Regex pattern for GSTIN format validation
   pattern: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,

   // State codes mapping (can be extended with all states)
   validStateCodes: new Set([
      '01',
      '02',
      '03',
      '04',
      '05',
      '06',
      '07',
      '08',
      '09',
      '10',
      '11',
      '12',
      '13',
      '14',
      '15',
      '16',
      '17',
      '18',
      '19',
      '20',
      '21',
      '22',
      '23',
      '24',
      '25',
      '26',
      '27',
      '28',
      '29',
      '30',
      '31',
      '32',
      '33',
      '34',
      '35',
      '36',
      '37',
   ]),

   // Main validation function
   isValid(gstin) {
      try {
         if (!gstin || typeof gstin !== 'string') {
            return false;
         }

         // Convert to uppercase for consistency
         gstin = gstin.toUpperCase();

         // Basic format check
         if (!this.pattern.test(gstin)) {
            return false;
         }

         // State code validation
         const stateCode = gstin.substring(0, 2);
         if (!this.validStateCodes.has(stateCode)) {
            return false;
         }

         // Checksum validation
         return this.validateChecksum(gstin);
      } catch (error) {
         console.error('GST Validation Error:', error);
         return false;
      }
   },

   // Checksum validation implementation
   validateChecksum(gstin) {
      const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
      const factor = [1, 2, 1, 2, 1, 2, 1, 2, 1, 2, 1, 2, 1, 2];
      let sum = 0;
      let checksum = chars.indexOf(gstin[14]);

      for (let i = 0; i < 14; i++) {
         let product = factor[i] * chars.indexOf(gstin[i]);
         sum += Math.floor(product / chars.length) + (product % chars.length);
      }

      let calculatedChecksum = (chars.length - (sum % chars.length)) % chars.length;
      return calculatedChecksum === checksum;
   },
};

export function formatAmount(amount) {
   const num = Number(amount);
   if (!Number.isFinite(num)) return 0;
   return Number.isInteger(num) ? num : parseFloat(num.toFixed(2));
}

/**
 * Filters stock items based on a search query
 * @param {Array<Object>} stocks - Array of stock objects with itemName and serialNo properties
 * @param {string} searchQuery - The search term to filter by
 * @returns {Array<Object>} Filtered array of stock objects
 */
export const filterStocks = (stocks, searchQuery) => {
   if (!searchQuery) return stocks; // If no search query, return all stocks

   const query = searchQuery.toLowerCase();

   return stocks.filter(
      (item) =>
         item.itemName?.toLowerCase().includes(query) ||
         item.serialNo?.toLowerCase().includes(query) ||
         item.barcode?.toLowerCase().includes(query),
   );
};

export function removeDuplicates(array, key) {
   if (!array || array.length === 0) return [];
   return array.filter((item, index, self) => item && index === self.findIndex((obj) => obj && obj[key] === item[key]));
}

export function buildRoleHierarchy(rolesObject) {
   const roles = Object.values(rolesObject);

   // Create a map to store roles by their roleId
   const roleMap = new Map();

   // Initialize all roles in the map
   roles.forEach((role) => {
      role.children = [];
      roleMap.set(role.roleId, role);
   });

   // Create the hierarchy by linking children to their parents
   const rootRoles = [];
   roles.forEach((role) => {
      if (roleMap.has(role.parentroleId)) {
         const parentRole = roleMap.get(role.parentroleId);
         if (parentRole.roleId === role.parentroleId) {
            parentRole.children.push(role);
         }
      } else {
         rootRoles.push(role);
      }
   });

   return rootRoles;
}

export function getDefaultId(data, key = 'DEFAULT') {
   if (!Array.isArray(data)) {
      console.error('Data must be an array');
      return null;
   }
   if (data.length === 0) return null;

   const normalizedKey = key.toUpperCase();
   const foundItem = data.find((item) => item.name.toUpperCase() === normalizedKey);

   return foundItem ? foundItem.id : null;
}

export function getAccountAddress(accountList, accountId) {
   if (!accountList?.length || !accountId) return null;

   const account = accountList.find((item) => item.id === accountId);
   if (!account) return null;

   const getAddress = (type) => account?.accountaddress?.find((item) => item.name === type);

   return {
      billingAddress: getAddress('billing'),
      shippingAddress: getAddress('shipping'),
   };
}

export function isEmpty(value) {
   if (value === null || value === undefined) return true;
   if (typeof value === 'string' && value.trim() === '') return true;
   if (Array.isArray(value) && value.length === 0) return true;
   if (typeof value === 'object' && Object.keys(value).length === 0) return true;
   return false;
}

export function getGstTaxType(company, party, type) {
   if (isEmpty(company.gstRegNo)) return GST_TXN_TYPE.None;

   let gstTxnType = GST_TXN_TYPE.None;

   if (!isEmpty(party.gstRegNo)) gstTxnType = GST_TXN_TYPE.B2B;
   else {
      if (type === 'sales') gstTxnType = GST_TXN_TYPE.B2C;
      else if (type === 'purchase') gstTxnType = GST_TXN_TYPE.Unregistered;
   }

   if (company.countryId !== party.countryId) {
      if (type === 'sales') gstTxnType = GST_TXN_TYPE.Exports;
      else if (type === 'purchase') gstTxnType = GST_TXN_TYPE.Imports;
   }

   return gstTxnType;
}

export function getGstSubTaxType(company, party, type) {
   if (isEmpty(company.gstRegNo)) return GST_SUB_TXN_TYPE.None;
   let gstSubTxnType = GST_SUB_TXN_TYPE.None;
   if (company.stateid === party.stateId) {
      gstSubTxnType = GST_SUB_TXN_TYPE.IntraState;
   } else {
      gstSubTxnType = GST_SUB_TXN_TYPE.InterState;
   }

   if (company.countryId !== party.countryId) {
      if (type === 'sales') gstSubTxnType = GST_SUB_TXN_TYPE.NormalExport;
      else if (type === 'purchase') gstSubTxnType = GST_SUB_TXN_TYPE.NormalImport;
   }

   return gstSubTxnType;
}

export function getCompanyById(company, id) {
   if (!Array.isArray(company) || company.length === 0 || !Number.isInteger(id) || id < 1) {
      return null;
   }
   return company.find((x) => x.id === id) || null;
}

export function calculateTaxAmount(row, billTerm) {
   const newRow = { ...row };

   newRow.lineItem.map((item) => {
      const qty1 = Number(item.qty1 || 0);
      const itemRate = Number(item.itemRate || 0);
      const amount = qty1 * itemRate;
      let netAmount = amount;

      // discount calc - addLess1Percent, addLess2Percent, addLess3Percent
      const discount1 = (amount * item.addLess1Percent) / 100;
      const discount2 = ((amount - item.addLess1Amount) * item.addLess1Percent) / 100;
      const discount3 = ((amount - item.addLess1Amount - item.addLess2Amount) * item.addLess2Percent) / 100;

      netAmount = amount - discount1 - discount2 - discount3;

      return {
         ...item,
         amount,
         netAmount,
      };
   });
}

export function setAllPermissionsTrue(roles, isTrue) {
   return roles.map((role) => ({
      ...role,
      add: isTrue,
      edit: isTrue,
      deleteRole: isTrue,
      print: isTrue,
      view: isTrue,
      children: role.children && role.children.length > 0 ? setAllPermissionsTrue(role.children, isTrue) : [],
   }));
}

export function setAllActionPermissions(roles, permission, isTrue) {
   roles.forEach((role) => {
      role[permission] = isTrue;

      if (role.children && role.children.length > 0) {
         setAllActionPermissions(role.children, permission, isTrue); // Recursive for nested children
      }
   });
   return roles;
}

export function hideMobileNumber(number) {
   const numStr = number.toString();
   if (numStr.length < 4) {
      return 'Invalid Number';
   }
   const hiddenPart = '*'.repeat(numStr.length - 4);
   const visiblePart = numStr.slice(-4);
   return hiddenPart + visiblePart;
}

export const getShareCompanyOptions = ({ dbName, company }) => {
   // If company is not valid, return empty array to prevent errors
   if (!company || !Array.isArray(company)) {
      return [];
   }

   // Check if dbName exists and is not 'undefined'
   if (dbName && dbName !== 'undefined') {
      // If dbName exists and is valid, only show the shared company that matches dbName
      return company
         .filter((comp) => comp.isShared && comp.dbName === dbName)
         .map((comp) => ({
            value: comp.id,
            label: comp.name,
         }));
   } else {
      // If dbName is null, undefined, or 'undefined' string, exclude all shared companies
      return company
         .filter((comp) => !comp.isShared)
         .map((comp) => ({
            value: comp.id,
            label: comp.name,
         }));
   }
};

export function deleteRecord(id, deleteFunc, dispatch, companyId, userGlobalSettingsData, handleRefreshData) {
   Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
   }).then(async (result) => {
      if (!result.isConfirmed) return;

      if (dispatch) {
         const shouldSendCompanyIdDaybook = userGlobalSettingsData?.[0]?.askCompany?.daybook || false;
         const shouldSendCompanyIdAccount = userGlobalSettingsData?.[0]?.askCompany?.account || false;
         const shouldSendCompanyIdItem = userGlobalSettingsData?.[0]?.askCompany?.item || false;
         await dispatch(deleteFunc(id));
         handleRefreshData && handleRefreshData();
         if (companyId) {
            dispatch(getDaybook({
               companyId: Number(companyId),
               ...(shouldSendCompanyIdDaybook && { companyIds: companyId }),
            }));
            dispatch(getItem({
               companyId,
               sortBy: 'name',
               sortOrder: 'asc',
               ...(shouldSendCompanyIdItem && { companyIds: companyId }),
            }));
            dispatch(getAccount({
               compId: Number(companyId),
               accountTypes: ['K', 'E', 'C', 'D', 'BROKER', 'I', 'L', 'M', 'N', 'A'].join(','),
               status: true,
               ...(shouldSendCompanyIdAccount && { companyIds: companyId }),
            }));
         }
      } else {
         await deleteFunc(id);
         handleRefreshData && handleRefreshData();
      }
   });
}

export async function deleteRecordForSales(id, daybookId, accountId, deleteFunc, dispatch, companyId, userGlobalSettingsData) {
   try {
      // First call getAdjustedDocs API to check for adjustments
      const response = await dispatch(
         getAdjustedDocs({
            spId: id,
            companyId: companyId,
            daybookId: daybookId,
            accountId: accountId,
            type: 'SALES' || undefined,
         }),
      );

      // Check if there are any adjusted documents
      if (response && response.data && response.data.length > 0) {
         // Show custom modal with checkbox options for adjusted documents
         showAdjustmentModal(response.data, id, deleteFunc, dispatch, companyId, userGlobalSettingsData);
      } else {
         // No adjustments found, proceed with normal deletion
         showNormalDeleteConfirmation(id, deleteFunc, dispatch, companyId, userGlobalSettingsData);
      }
   } catch (error) {
      console.error('Error fetching adjusted documents:', error);
      // If API call fails, proceed with normal deletion
      showNormalDeleteConfirmation(id, deleteFunc, dispatch, companyId, userGlobalSettingsData);
   }
}

function showAdjustmentModal(adjustedDocs, id, deleteFunc, dispatch, companyId, userGlobalSettingsData) {
   // Create HTML content for the modal with Tailwind CSS styling
   let htmlContent = `
      <div class="adjustment-modal">
         <!-- Warning Alert -->
         <div class="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <div class="flex items-center">
               <div class="flex-shrink-0">
                  <i class="fas fa-exclamation-triangle text-amber-500 text-xl"></i>
               </div>
               <div class="ml-3">
                  <p class="text-sm text-amber-800">
                     <span class="font-semibold">Warning:</span> The following adjusted documents will be affected by this deletion.
                  </p>
               </div>
            </div>
         </div>
         
         <!-- Header Section -->
         <div class="flex items-center justify-between mb-6">
            <div>
               <h3 class="text-lg font-medium text-gray-700">${adjustedDocs.length} adjustment(s) found:</h3>
            </div>
            <div class="flex space-x-2">
               <button type="button" class="inline-flex items-center px-3 py-2 border border-blue-300 shadow-sm text-sm leading-4 font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200" id="selectAllBtn">
                  <i class="fas fa-check-square mr-2"></i>
                  Select All
               </button>
               <button type="button" class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200" id="deselectAllBtn">
                  <i class="fas fa-square mr-2"></i>
                  Deselect All
               </button>
            </div>
         </div>
         
         <!-- Table Container -->
         <div class="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden mb-6">
            <div class="max-h-80 overflow-y-auto">
               <table class="min-w-full divide-y divide-gray-200">
                  <thead class="bg-gray-50 sticky top-0 z-10">
                     <tr>
                        <th class="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-16">
                           <input type="checkbox" id="masterCheckbox" class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded">
                        </th>
                        <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Doc No</th>
                        <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date</th>
                        <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Daybook</th>
                        <th class="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Amount</th>
                     </tr>
                  </thead>
                  <tbody class="bg-white divide-y divide-gray-200">
   `;

   adjustedDocs.forEach((doc, index) => {
      // Get the outAdjustmentId from the document
      const outAdjustmentId = doc.outAdjustmentId || doc.id || doc.adjustmentId || null;
      const hasValidId = outAdjustmentId && outAdjustmentId !== 'null' && outAdjustmentId !== 'undefined';

      htmlContent += `
         <tr class="hover:bg-gray-50 cursor-pointer transition-colors duration-150 ${!hasValidId ? 'bg-yellow-50' : ''
         }" data-index="${index}">
            <td class="px-4 py-4 text-center">
               <input
                  class="adjustment-checkbox h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  type="checkbox"
                  value="${outAdjustmentId || index}"
                  id="adjustment-${outAdjustmentId || index}"
                  data-out-adjustment-id="${outAdjustmentId}"
                  data-original-doc='${JSON.stringify(doc).replace(/'/g, '&apos;')}'
                  ${hasValidId ? '' : 'disabled'}
               >
            </td>
            <td class="px-4 py-4 text-left">
               <div class="text-sm font-semibold text-blue-600">${doc.docno || doc.docNo || doc.documentNo || 'N/A'
         }</div>
            </td>
            <td class="px-4 py-4 text-left">
               <div class="text-sm text-gray-600">${doc.txnDate ? new Date(doc.txnDate).toLocaleDateString('en-GB') : 'N/A'
         }</div>
            </td>
            <td class="px-4 py-4 text-left">
               <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  ${doc.adj_daybookname || 'N/A'}
               </span>
            </td>
            <td class="px-4 py-4 text-right">
               <div class="text-sm font-semibold text-green-600">₹${parseFloat(doc.amount || 0).toLocaleString(
            'en-IN',
         )}</div>
            </td>
         </tr>
      `;
   });

   htmlContent += `
                  </tbody>
               </table>
            </div>
         </div>
         
         <!-- Action Options Section -->
         <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div class="flex items-start">
               <div class="flex-shrink-0">
                  <i class="fas fa-info-circle text-blue-500 text-lg"></i>
               </div>
               <div class="ml-3">
                  <h4 class="text-sm font-semibold text-blue-800 mb-2">Deletion Options:</h4>
                  <ul class="text-sm text-blue-700 space-y-1">
                     <li>• <span class="font-medium">Delete Sales Only:</span> Delete only the sales record without affecting adjusted documents</li>
                     <li>• <span class="font-medium">Delete Selected Adjustments:</span> Select adjustments above and delete them along with the sales record</li>
                  </ul>
               </div>
            </div>
         </div>        
      </div>
   `;

   Swal.fire({
      title: '<span class="text-xl font-semibold text-gray-800">Adjusted Documents Found</span>',
      html: htmlContent,
      icon: 'warning',
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonColor: '#dc2626',
      denyButtonColor: '#2563eb',
      cancelButtonColor: '#6b7280',
      confirmButtonText: '<i class="fas fa-trash mr-2"></i>Delete Selected Adjustments',
      denyButtonText: '<i class="fas fa-file-minus mr-2"></i>Delete Sales Only',
      cancelButtonText: '<i class="fas fa-times mr-2"></i>Cancel',
      width: '70%',
      maxWidth: '800px',
      padding: '2rem',
      customClass: {
         popup: 'adjustment-modal-popup',
         confirmButton:
            'px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-md shadow-sm transition-colors duration-200 mr-2',
         denyButton:
            'px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md shadow-sm transition-colors duration-200 mr-2',
         cancelButton:
            'px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white font-medium rounded-md shadow-sm transition-colors duration-200',
      },
      buttonsStyling: false,
      didOpen: () => {
         const selectAllBtn = document.getElementById('selectAllBtn');
         const deselectAllBtn = document.getElementById('deselectAllBtn');
         const masterCheckbox = document.getElementById('masterCheckbox');
         const checkboxes = document.querySelectorAll('.adjustment-checkbox:not(:disabled)');

         // Get the SweetAlert buttons
         const confirmButton = document.querySelector('.swal2-confirm');
         const denyButton = document.querySelector('.swal2-deny');

         // Add custom styles for selected rows
         const style = document.createElement('style');
         style.textContent = `
            .row-selected {
               background-color: #dcfce7 !important;
               border-left: 4px solid #16a34a;
            }
            .row-selected:hover {
               background-color: #bbf7d0 !important;
            }
            .swal2-popup.adjustment-modal-popup {
               border-radius: 0.75rem !important;
               box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
            }
            .adjustment-checkbox:disabled {
               opacity: 0.5;
               cursor: not-allowed;
            }
            .button-hidden {
               display: none !important;
            }
         `;
         document.head.appendChild(style);

         // Master checkbox functionality
         masterCheckbox.addEventListener('change', () => {
            checkboxes.forEach((checkbox) => {
               checkbox.checked = masterCheckbox.checked;
            });
            updateButtonStates();
         });

         // Select All functionality
         selectAllBtn.onclick = () => {
            checkboxes.forEach((checkbox) => {
               checkbox.checked = true;
            });
            masterCheckbox.checked = true;
            updateButtonStates();
         };

         // Deselect All functionality
         deselectAllBtn.onclick = () => {
            checkboxes.forEach((checkbox) => {
               checkbox.checked = false;
            });
            masterCheckbox.checked = false;
            updateButtonStates();
         };

         // Update button states based on selection
         function updateButtonStates() {
            const checkedCount = document.querySelectorAll('.adjustment-checkbox:checked').length;
            const totalCount = checkboxes.length;

            // Update master checkbox state
            if (checkedCount === totalCount && totalCount > 0) {
               masterCheckbox.checked = true;
               masterCheckbox.indeterminate = false;
            } else if (checkedCount === 0) {
               masterCheckbox.checked = false;
               masterCheckbox.indeterminate = false;
            } else {
               masterCheckbox.checked = false;
               masterCheckbox.indeterminate = true;
            }

            // Update Select All / Deselect All button states
            if (checkedCount === totalCount) {
               selectAllBtn.disabled = true;
               selectAllBtn.classList.add('opacity-50', 'cursor-not-allowed');
               selectAllBtn.classList.remove('hover:bg-blue-50');
               deselectAllBtn.disabled = false;
               deselectAllBtn.classList.remove('opacity-50', 'cursor-not-allowed');
               deselectAllBtn.classList.add('hover:bg-gray-50');
            } else if (checkedCount === 0) {
               selectAllBtn.disabled = false;
               selectAllBtn.classList.remove('opacity-50', 'cursor-not-allowed');
               selectAllBtn.classList.add('hover:bg-blue-50');
               deselectAllBtn.disabled = true;
               deselectAllBtn.classList.add('opacity-50', 'cursor-not-allowed');
               deselectAllBtn.classList.remove('hover:bg-gray-50');
            } else {
               selectAllBtn.disabled = false;
               selectAllBtn.classList.remove('opacity-50', 'cursor-not-allowed');
               selectAllBtn.classList.add('hover:bg-blue-50');
               deselectAllBtn.disabled = false;
               deselectAllBtn.classList.remove('opacity-50', 'cursor-not-allowed');
               deselectAllBtn.classList.add('hover:bg-gray-50');
            }

            // Dynamic button visibility logic
            if (checkedCount === 0) {
               // No records selected - Hide "Delete Selected Adjustments", Show "Delete Sales Only"
               confirmButton.classList.add('button-hidden');
               denyButton.classList.remove('button-hidden');
               denyButton.innerHTML = '<i class="fas fa-file-minus mr-2"></i>Delete Sales Only';
            } else {
               // Records selected - Show "Delete Selected Adjustments", Hide "Delete Sales Only"
               confirmButton.classList.remove('button-hidden');
               denyButton.classList.add('button-hidden');
               confirmButton.innerHTML = `<i class="fas fa-trash mr-2"></i>Delete Selected Adjustments (${checkedCount})`;

               // Ensure the confirm button is enabled and styled properly
               confirmButton.disabled = false;
               confirmButton.classList.remove('opacity-50', 'cursor-not-allowed');
               confirmButton.classList.add('hover:bg-red-700');
            }

            // Update row highlighting with Tailwind classes
            document.querySelectorAll('.adjustment-checkbox').forEach((cb) => {
               const row = cb.closest('tr');
               if (cb.checked && !cb.disabled) {
                  row.classList.add('row-selected');
                  row.classList.remove('bg-yellow-50');
               } else if (!cb.disabled) {
                  row.classList.remove('row-selected');
               }
            });
         }

         // Add change event listeners to checkboxes
         checkboxes.forEach((checkbox) => {
            checkbox.addEventListener('change', updateButtonStates);
         });

         // Initial button state update
         updateButtonStates();

         // Row click functionality
         document.querySelectorAll('tbody tr').forEach((row) => {
            row.addEventListener('click', (e) => {
               if (e.target.type !== 'checkbox') {
                  const checkbox = row.querySelector('.adjustment-checkbox');
                  // Add null check before accessing properties
                  if (checkbox && !checkbox.disabled) {
                     checkbox.checked = !checkbox.checked;
                     updateButtonStates();
                  }
               }
            });
         });
      },
      preConfirm: () => {
         // This is for "Delete Selected Adjustments" button
         // Get all selected checkboxes
         const selectedCheckboxes = document.querySelectorAll('.adjustment-checkbox:checked');
         const selectedOutAdjustmentIds = [];

         selectedCheckboxes.forEach((cb) => {
            // Get the outAdjustmentId from data attribute
            const outAdjustmentId = cb.getAttribute('data-out-adjustment-id');

            // Only add to array if outAdjustmentId exists and is not null/undefined
            if (outAdjustmentId && outAdjustmentId !== 'null' && outAdjustmentId !== 'undefined') {
               // Convert to number if it's a numeric string, otherwise keep as string
               const idValue = isNaN(outAdjustmentId) ? outAdjustmentId : Number(outAdjustmentId);
               selectedOutAdjustmentIds.push(idValue);
            }
         });

         // Return the selected adjustment IDs (can be empty array if none selected)
         return selectedOutAdjustmentIds;
      },
      preDeny: () => {
         // This is for "Delete Sales Only" button
         // Return a special marker to indicate sales-only deletion
         return 'SALES_ONLY';
      },
   }).then((result) => {
      if (result.isConfirmed) {
         // Delete Selected Adjustments button was clicked
         const outAdjustmentIds = result.value || [];
         try {
            if (dispatch) {
               const shouldSendCompanyIdDaybook = userGlobalSettingsData?.[0]?.askCompany?.daybook || false;
               const shouldSendCompanyIdAccount = userGlobalSettingsData?.[0]?.askCompany?.account || false;
               const shouldSendCompanyIdItem = userGlobalSettingsData?.[0]?.askCompany?.item || false;
               // Pass id as first param and outAdjustmentIds array directly as second param
               dispatch(deleteFunc(id, outAdjustmentIds));
               if (companyId) {
                  dispatch(
                     getDaybook({
                        companyId: Number(companyId),
                        ...(shouldSendCompanyIdDaybook && { companyIds: companyId }),
                     }),
                  );
                  dispatch(
                     getItem({
                        companyId: companyId,
                        sortBy: 'name',
                        sortOrder: 'asc',
                        ...(shouldSendCompanyIdItem && { companyIds: companyId }),
                     }),
                  );
                  dispatch(
                     getAccount({
                        compId: Number(companyId),
                        accountTypes: ['K', 'E', 'C', 'D', 'BROKER', 'I', 'L', 'M', 'N', 'A'].join(','),
                        status: true,
                        ...(shouldSendCompanyIdAccount && { companyIds: companyId }),
                     }),
                  );
               }
            } else {
               // Pass id as first param and outAdjustmentIds array directly as second param
               deleteFunc(id, outAdjustmentIds);
            }

            // Show success message with Tailwind styling
            const message =
               outAdjustmentIds.length > 0
                  ? `Deleting record with <span class="font-semibold text-blue-600">${outAdjustmentIds.length}</span> selected adjustment(s)...`
                  : `Deleting sales record with <span class="font-semibold text-orange-600">no adjustments</span> selected...`;

            Swal.fire({
               title: '<span class="text-xl font-semibold text-blue-600">Deletion In Progress!</span>',
               html: `<p class="text-gray-600">${message}</p>`,
               icon: 'info',
               timer: 2000,
               showConfirmButton: false,
               customClass: {
                  popup: 'rounded-xl shadow-2xl',
               },
            });
         } catch (error) {
            console.error('Error during deletion:', error);
            Swal.fire({
               title: '<span class="text-xl font-semibold text-red-600">Error!</span>',
               html: '<p class="text-gray-600">An error occurred during deletion. Please check the console for details.</p>',
               icon: 'error',
               customClass: {
                  popup: 'rounded-xl shadow-2xl',
               },
            });
         }
      } else if (result.isDenied) {
         // Delete Sales Only button was clicked
         try {
            if (dispatch) {
               // Pass only the id, no adjustment IDs (or pass empty array to indicate sales-only deletion)
               dispatch(deleteFunc(id, []));
            } else {
               // Pass only the id, no adjustment IDs (or pass empty array to indicate sales-only deletion)
               deleteFunc(id, []);
            }

            // Show success message for sales-only deletion
            Swal.fire({
               title: '<span class="text-xl font-semibold text-blue-600">Sales Deletion In Progress!</span>',
               html: '<p class="text-gray-600">Deleting <span class="font-semibold text-blue-600">sales record only</span> without affecting adjustments...</p>',
               icon: 'info',
               timer: 2000,
               showConfirmButton: false,
               customClass: {
                  popup: 'rounded-xl shadow-2xl',
               },
            });
         } catch (error) {
            console.error('Error during sales-only deletion:', error);
            Swal.fire({
               title: '<span class="text-xl font-semibold text-red-600">Error!</span>',
               html: '<p class="text-gray-600">An error occurred during sales deletion. Please check the console for details.</p>',
               icon: 'error',
               customClass: {
                  popup: 'rounded-xl shadow-2xl',
               },
            });
         }
      }
   });
}

function showNormalDeleteConfirmation(id, deleteFunc, dispatch, companyId, userGlobalSettingsData) {
   Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this action!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: '<i class="fas fa-trash me-1"></i> Yes, delete it!',
      cancelButtonText: '<i class="fas fa-times me-1"></i> Cancel',
   }).then(async (result) => {
      if (result.isConfirmed) {
         const shouldSendCompanyIdDaybook = userGlobalSettingsData?.[0]?.askCompany?.daybook || false;
         const shouldSendCompanyIdAccount = userGlobalSettingsData?.[0]?.askCompany?.account || false;
         const shouldSendCompanyIdItem = userGlobalSettingsData?.[0]?.askCompany?.item || false;
         // Pass a callback function to handle success
         const handleSuccess = () => {
            Swal.fire({
               title: 'Deleted Successfully!',
               text: 'Record has been deleted successfully.',
               icon: 'success',
               timer: 3000,
               showConfirmButton: false,
            });
         };

         const handleError = (errorMessage) => {
            Swal.fire({
               title: 'Delete Failed!',
               text: errorMessage || 'Something went wrong.',
               icon: 'error',
               timer: 3000,
               showConfirmButton: false,
            });
         };

         if (dispatch) {
            await dispatch(deleteFunc(id, handleSuccess, handleError));
            if (companyId) {
               await dispatch(
                  getDaybook({
                     companyId: Number(companyId),
                     ...(shouldSendCompanyIdDaybook && { companyIds: companyId }),
                  }),
               );
               await dispatch(
                  getItem({
                     companyId: companyId,
                     sortBy: 'name',
                     sortOrder: 'asc',
                     ...(shouldSendCompanyIdItem && { companyIds: companyId }),
                  }),
               );
               await dispatch(
                  getAccount({
                     compId: Number(companyId),
                     accountTypes: ['K', 'E', 'C', 'D', 'BROKER', 'I', 'L', 'M', 'N', 'A'].join(','),
                     status: true,
                     ...(shouldSendCompanyIdAccount && { companyIds: companyId }),
                  }),
               );
            }
         } else {
            deleteFunc(id, handleSuccess, handleError);
         }
      }
   });
}

export function eBillDateFormat(date) {
   return moment(date).local().format('DD/MM/YYYY, h:mm:ss A');
}

export const handlePrintClick = (singleEntity, html, css, navigate, redirect) => {
   // Formatting utility functions
   const formatCurrency = (value) => {
      if (!value) return '0.00';
      return parseFloat(value).toFixed(2);
   };

   const formatDate = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
         day: '2-digit',
         month: '2-digit',
         year: 'numeric',
      });
   };

   const amountInWords = (amount) => {
      // Simple implementation - in a real app, use a proper library
      const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
      const teens = [
         'Ten',
         'Eleven',
         'Twelve',
         'Thirteen',
         'Fourteen',
         'Fifteen',
         'Sixteen',
         'Seventeen',
         'Eighteen',
         'Nineteen',
      ];
      const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

      // This is a simplified version - in production use a proper library
      if (amount < 1000) {
         return `${amount} Rupees Only`;
      } else {
         return `${Math.floor(amount / 1000)} Thousand ${amount % 1000} Rupees Only`;
      }
   };

   const getTaxRate = (item) => {
      if (!item) return '0%';
      const gstPercent = item.gstPercent || 0;
      return `${gstPercent}%`;
   };

   const getTaxType = (item) => {
      if (!item) return 'N/A';
      // Add proper tax type logic based on your data structure
      return item.typeOfSupply || 'Goods';
   };

   const getTaxAmount = (item) => {
      if (!item) return '0.00';
      const netGst = item.netGst || 0;
      return formatCurrency(netGst);
   };

   // Format dates
   const formattedTransactionDate = formatDate(singleEntity.transactionAt);
   const formattedCreatedAtnDate = formatDate(singleEntity.createdAt);

   // Calculate totals
   const totals = {
      totalTaxAmount: singleEntity.gstAmount || 0,
      grandTotal: singleEntity.netAmount || 0,
   };

   // Use the HTML data from singleEntity or generate new HTML if not available
   const htmlData = `
 <!DOCTYPE html>
 <html>
 <head>
   <title>Invoice - ${singleEntity.docno || ''}</title>
   <meta charset="UTF-8">
   <meta name="viewport" content="width=device-width, initial-scale=1.0">
   <style>
     ${css}
   </style>
 </head>
 <body>
   ${html}
 </body>
 </html>
 `;

   // Create a temporary iframe to render the HTML
   const printAndGeneratePDF = (htmlContent) => {
      // Extract CSS from HTML content
      const cssStyles = css; // Use the existing cssData variable

      // First approach: Print directly using browser print functionality
      const printDirectly = () => {
         const iframe = document.createElement('iframe');
         iframe.style.display = 'none';
         document.body.appendChild(iframe);

         iframe.contentDocument.open();
         iframe.contentDocument.write(htmlContent);
         iframe.contentDocument.close();

         // Wait for all resources to load before printing
         iframe.onload = () => {
            try {
               iframe.contentWindow.print();
               // Remove the iframe after printing (or after a delay to ensure printing completes)
               setTimeout(() => {
                  document.body.removeChild(iframe);
               }, 1000);
            } catch (error) {
               console.error('Error printing:', error);
               document.body.removeChild(iframe);
            }
         };
      };

      // Second approach: Generate PDF using jsPDF and html2canvas
      const generatePDF = async () => {
         try {
            // Check if the libraries are loaded
            if (!window.html2canvas || !window.jspdf) {
               await loadPDFLibraries();
            }

            const { jsPDF } = window.jspdf;

            // Create a clean document viewer
            const printWindow = window.open('', '_blank');
            if (!printWindow) {
               alert('Please allow pop-ups to generate the PDF');
               return;
            }

            // Write the complete HTML with CSS to the new window
            printWindow.document.write(`
           <!DOCTYPE html>
           <html>
           <head>
             <title>Invoice - ${singleEntity.docno || ''}</title>
             <meta charset="UTF-8">
             <meta name="viewport" content="width=device-width, initial-scale=1.0">
             <style>${cssStyles}</style>
           </head>
           <body>
             <div class="invoice-container">
               ${printWindow.document.documentElement.querySelector('.invoice-container')
                  ? printWindow.document.documentElement.querySelector('.invoice-container').innerHTML
                  : htmlContent.match(/<div class="invoice-container">([\s\S]*?)<\/div>/)?.[1] || htmlContent
               }
             </div>
             <script>
               document.addEventListener('DOMContentLoaded', function() {
                 // Alert to let us know DOM is ready
                 window.isReady = true;
               });
             </script>
           </body>
           </html>
         `);

            // Give the window time to render the content
            setTimeout(async () => {
               try {
                  const invoiceContainer = printWindow.document.querySelector('.invoice-container');

                  if (!invoiceContainer) {
                     throw new Error('Could not find invoice container in the document');
                  }

                  // Create a new PDF document in A4 format
                  const pdf = new jsPDF('p', 'pt', 'a4');
                  const pageWidth = pdf.internal.pageSize.getWidth();
                  const pageHeight = pdf.internal.pageSize.getHeight();

                  // Capture HTML content as an image using html2canvas
                  const canvas = await html2canvas(invoiceContainer, {
                     scale: 2, // Higher scale for better quality
                     logging: false,
                     useCORS: true,
                     windowWidth: invoiceContainer.scrollWidth,
                  });

                  // Calculate the number of pages needed
                  const imgHeight = (canvas.height * pageWidth) / canvas.width;
                  const totalPages = Math.ceil(imgHeight / pageHeight);

                  // Add each page to the PDF
                  let position = 0;

                  // For the first page
                  pdf.addImage(
                     canvas.toDataURL('image/png', 1.0),
                     'PNG',
                     0,
                     position,
                     pageWidth,
                     (pageWidth * canvas.height) / canvas.width,
                  );

                  // For additional pages if content is longer than one page
                  for (let i = 1; i < totalPages; i++) {
                     position -= pageHeight;
                     pdf.addPage();
                     pdf.addImage(
                        canvas.toDataURL('image/png', 1.0),
                        'PNG',
                        0,
                        position,
                        pageWidth,
                        (pageWidth * canvas.height) / canvas.width,
                     );
                  }

                  // Save the PDF
                  const fileName = `Invoice-${singleEntity.docno || 'Document'}-${new Date()
                     .toISOString()
                     .slice(0, 10)}.pdf`;
                  pdf.save(fileName);

                  // Close the temp window
                  printWindow.close();
               } catch (error) {
                  console.error('Error generating PDF:', error);
                  alert(`Error generating PDF: ${error.message}`);
                  printWindow.close();
               }
            }, 1000); // Increased timeout to allow proper rendering
         } catch (error) {
            console.error('PDF generation failed:', error);
            alert('PDF generation failed. Please try again or use the print function instead.');
         }
      };

      // Function to load necessary libraries if they're not already loaded
      const loadPDFLibraries = () => {
         return new Promise((resolve, reject) => {
            // Load html2canvas if not already loaded
            if (!window.html2canvas) {
               const html2canvasScript = document.createElement('script');
               html2canvasScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
               html2canvasScript.onload = () => {
                  // After html2canvas is loaded, load jsPDF
                  if (!window.jspdf) {
                     const jsPDFScript = document.createElement('script');
                     jsPDFScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
                     jsPDFScript.onload = resolve;
                     jsPDFScript.onerror = reject;
                     document.head.appendChild(jsPDFScript);
                  } else {
                     resolve();
                  }
               };
               html2canvasScript.onerror = reject;
               document.head.appendChild(html2canvasScript);
            }
            // If html2canvas is loaded but jsPDF is not
            else if (!window.jspdf) {
               const jsPDFScript = document.createElement('script');
               jsPDFScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
               jsPDFScript.onload = resolve;
               jsPDFScript.onerror = reject;
               document.head.appendChild(jsPDFScript);
            }
            // Both are already loaded
            else {
               resolve();
            }
         });
      };

      // Create action buttons for printing and generating PDF
      const showActionButtons = () => {
         // Create a container for the buttons
         const actionContainer = document.createElement('div');
         actionContainer.style.position = 'fixed';
         actionContainer.style.top = '20px';
         actionContainer.style.right = '20px';
         actionContainer.style.zIndex = '9999';
         actionContainer.style.display = 'flex';
         actionContainer.style.flexDirection = 'column';
         actionContainer.style.gap = '10px';

         // Create Print button
         const printButton = document.createElement('button');
         printButton.textContent = 'Print Invoice';
         printButton.style.padding = '10px 15px';
         printButton.style.backgroundColor = '#4CAF50';
         printButton.style.color = 'white';
         printButton.style.border = 'none';
         printButton.style.borderRadius = '4px';
         printButton.style.cursor = 'pointer';
         printButton.onclick = () => {
            printDirectly();
            navigate(redirect);
         };

         // Create PDF button
         const pdfButton = document.createElement('button');
         pdfButton.textContent = 'Generate PDF';
         pdfButton.style.padding = '10px 15px';
         pdfButton.style.backgroundColor = '#2196F3';
         pdfButton.style.color = 'white';
         pdfButton.style.border = 'none';
         pdfButton.style.borderRadius = '4px';
         pdfButton.style.cursor = 'pointer';
         pdfButton.onclick = () => {
            generatePDF();
            navigate(redirect);
         };

         // Create Close button
         const closeButton = document.createElement('button');
         closeButton.textContent = 'Close Preview';
         closeButton.style.padding = '10px 15px';
         closeButton.style.backgroundColor = '#f44336';
         closeButton.style.color = 'white';
         closeButton.style.border = 'none';
         closeButton.style.borderRadius = '4px';
         closeButton.style.cursor = 'pointer';
         closeButton.onclick = () => {
            document.body.removeChild(previewContainer);
            document.body.removeChild(actionContainer);
            document.body.style.overflow = 'auto';
            navigate(redirect);
         };

         // Add buttons to container
         actionContainer.appendChild(printButton);
         actionContainer.appendChild(pdfButton);
         actionContainer.appendChild(closeButton);

         // Add container to body
         document.body.appendChild(actionContainer);

         return actionContainer;
      };

      // Create a preview iframe for proper CSS rendering
      const previewContainer = document.createElement('div');
      previewContainer.style.position = 'fixed';
      previewContainer.style.top = '0';
      previewContainer.style.left = '0';
      previewContainer.style.width = '100%';
      previewContainer.style.height = '100%';
      previewContainer.style.backgroundColor = 'rgba(0,0,0,0.8)';
      previewContainer.style.zIndex = '9998';
      previewContainer.style.overflow = 'auto';
      previewContainer.style.padding = '50px';

      // Create an iframe for better style isolation
      const previewFrame = document.createElement('iframe');
      previewFrame.style.width = '100%';
      previewFrame.style.height = '100vh';
      previewFrame.style.border = 'none';
      previewFrame.style.backgroundColor = 'white';
      previewFrame.style.borderRadius = '5px';

      previewContainer.appendChild(previewFrame);
      document.body.appendChild(previewContainer);

      // Write content to the iframe with proper styling
      previewFrame.contentWindow.document.open();
      previewFrame.contentWindow.document.write(`
       <!DOCTYPE html>
       <html>
       <head>
         <title>Invoice Preview</title>
         <meta charset="UTF-8">
         <meta name="viewport" content="width=device-width, initial-scale=1.0">
         <style>${cssStyles}</style>
       </head>
       <body style="margin:0; padding:0;">
         ${htmlContent.match(/<body>([\s\S]*?)<\/body>/)?.[1] || htmlContent}
       </body>
       </html>
     `);
      previewFrame.contentWindow.document.close();

      // Prevent scrolling on the body
      document.body.style.overflow = 'hidden';

      // Show action buttons
      showActionButtons();
   };

   // Show the preview and action buttons
   printAndGeneratePDF(htmlData);
};

export function numberToWords(num) {
   if (num === 0) return 'zero';

   const belowTwenty = [
      '',
      'one',
      'two',
      'three',
      'four',
      'five',
      'six',
      'seven',
      'eight',
      'nine',
      'ten',
      'eleven',
      'twelve',
      'thirteen',
      'fourteen',
      'fifteen',
      'sixteen',
      'seventeen',
      'eighteen',
      'nineteen',
   ];

   const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

   const thousands = ['', 'thousand', 'million', 'billion'];

   function helper(n) {
      if (n === 0) return '';
      else if (n < 20) return belowTwenty[n] + ' ';
      else if (n < 100) return tens[Math.floor(n / 10)] + ' ' + helper(n % 10);
      else return belowTwenty[Math.floor(n / 100)] + ' hundred ' + helper(n % 100);
   }

   let word = '';
   let i = 0;

   while (num > 0) {
      if (num % 1000 !== 0) {
         word = helper(num % 1000) + thousands[i] + ' ' + word;
      }
      num = Math.floor(num / 1000);
      i++;
   }

   return word.trim();
}

export const formatDateIfValid = (input) => {
   if (!input) return '';
   const date = moment(input);
   if (date.isValid()) {
      return date.format('DD/MM/YYYY');
   }
   return input;
};

export function injectDataIntoHtml(htmlString, data) {
   const formatCurrency = (amount) => {
      return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount || 0);
   };

   const formatDateIfValid = (dateString) => {
      if (!dateString || dateString === 'A/N') return dateString;
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? dateString : date.toLocaleDateString('en-IN');
   };

   const numberToWords = (num) => {
      if (num === 0) return 'Zero Only';
      return `${num} Only`;
   };

   if (!data) {
      return htmlString;
   }

   // Handle both array and single object
   const invoice = Array.isArray(data) ? data[0] : data;

   const parser = new DOMParser();
   const doc = parser.parseFromString(htmlString, 'text/html');

   // Define all possible class names and their corresponding backend field mappings
   const classFieldMapping = {
      // Company Details
      companyname: 'companyname',
      CompanyAddress: 'companyaddress',
      CompanyMobile1value: 'companymobile1value',
      CompanyEmailValue: 'companyemailvalue',
      CompanyGstinValue: 'companygstinvalue',
      CompanyPanNoValue: 'companypannovalue',

      // Customer/Bill Details
      BillCustomerName: 'billcustomername',
      BillCustomerAddres: 'billcustomeraddress',
      CustomerCity: 'customercity',
      BillCustomerCountry: 'billcustomercountry',
      CustomerPanNoValue: 'customerpannovalue',
      BillCustomerGstinValue: 'billcustomergstinvalue',

      // Delivery Details
      DeliverCustomerName: 'delivercustomername',
      DeliveryAddress: 'deliveryaddress',
      DeliveryCity: 'deliverycity',
      DeliveryCountry: 'deliverycountry',
      DeliveryPanNoValue: 'deliverypannovalue',
      DeliveredCustGstinValue: 'deliveredcustgstinvalue',

      // Invoice Details
      InvoiceNoValue: 'invoicenovalue',
      InvoiceDtValue: 'invoicedtvalue',
      ChallanNoValue: 'challannovalue',
      ChallanDtValue: 'challandtvalue',
      PlaceofSupplyValue: 'placeofsupplyvalue',

      // Transport Details
      LRnoValue: 'lrnovalue',
      LRDtValue: 'lrdtvalue',
      TransportValue: 'transportvalue',
      VechileNoValue: 'vechilenovalue',
      EwayBillnoValue: 'ewaybillnovalue',
      EwaybillDtValue: 'ewaybilldtvalue',

      // Bank Details
      banknamevalue: 'banknamevalue',
      BankAccountNoValue: 'bankaccountnovalue',
      bankIfscvalue: 'bankifscvalue',

      // E-Invoice Details
      IrnValue: 'irnvalue',
      AcknoValue: 'acknovalue',

      // Tax Percentages
      CGSTpercentage: 'cgstpercentage',
      SGSTpercentage: 'sgstpercentage',
      IGSTpercentage: 'igstpercentage',

      // Tax Values
      CGSTValue: 'cgstvalue',
      SGSTValue: 'sgstvalue',
      IGSTValue: 'igstvalue',

      // Amount Values
      BillSubTotal: 'billsubtotal',
      TotalTaxablevalue: 'totaltaxablevalue',
      BillNetAmountValue: 'billnetamountvalue',
      TotalTotalAmount: 'totaltotalamount',

      // Line Item Fields
      SrNo: 'srno',
      ItemName: 'itemname',
      Qty1: 'qty1',
      Qty2: 'qty2',
      Qty3: 'qty3',
      Unit: 'unit',
      Rate: 'rate',
      ItemNetAmount: 'itemnetamount',
      GstPercentage: 'gstpercentage',
      GstAmount: 'gstamount',
      TotalAmount: 'totalamount',

      // Other Fields
      AmountInWords: 'amountinwords',
      TermsValue: 'termsvalue',
   };

   // Function to determine field type for formatting
   const getFieldType = (className, fieldName) => {
      const lowerClass = className.toLowerCase();
      const lowerField = fieldName.toLowerCase();

      if (lowerClass.includes('percentage') || lowerField.includes('percentage')) {
         return 'percentage';
      }
      if (lowerClass.includes('dtvalue') || lowerClass.includes('date') || lowerField.includes('date')) {
         return 'date';
      }
      if (
         lowerClass.includes('value') &&
         (lowerClass.includes('amount') ||
            lowerClass.includes('total') ||
            lowerClass.includes('cgst') ||
            lowerClass.includes('sgst') ||
            lowerClass.includes('igst') ||
            lowerClass.includes('rate') ||
            lowerField.includes('amount') ||
            lowerField.includes('total') ||
            lowerField.includes('rate'))
      ) {
         return 'currency';
      }
      return 'text';
   };

   // Function to format value based on type
   const formatValue = (value, type, className) => {
      if (value === null || value === undefined) {
         // Handle null/empty values with defaults
         if (
            className.toLowerCase().includes('transport') ||
            className.toLowerCase().includes('lr') ||
            className.toLowerCase().includes('eway') ||
            className.toLowerCase().includes('vechile')
         ) {
            return 'OFFICE DELIVERY';
         }
         if (className.toLowerCase().includes('bank')) {
            return 'N/A';
         }
         return '';
      }

      switch (type) {
         case 'currency':
            return formatCurrency(value);
         case 'percentage':
            return `${value}%`;
         case 'date':
            return formatDateIfValid(value);
         default:
            return value.toString();
      }
   };

   // Main function to populate data by class names
   const populateDataByClass = () => {
      // Iterate through all class mappings
      Object.keys(classFieldMapping).forEach((className) => {
         const fieldName = classFieldMapping[className];
         const fieldValue = invoice[fieldName];

         // Find all elements with this class
         const elements = doc.getElementsByClassName(className);

         if (elements.length > 0) {
            console.log(
               `Found ${elements.length} elements with class: ${className}, mapping to field: ${fieldName}, value: ${fieldValue}`,
            );

            // Get field type for formatting
            const fieldType = getFieldType(className, fieldName);

            // Format the value
            const formattedValue = formatValue(fieldValue, fieldType, className);

            // Update all elements with this class
            Array.from(elements).forEach((element) => {
               element.textContent = formattedValue;
            });
         }
      });
   };

   // Execute the main population function
   populateDataByClass();

   // Handle Line Items Table - Enhanced to preserve thead
   const handleLineItems = () => {
      // Multiple selector strategies to find the table body
      let lineItemsBody = null;

      // Try different approaches to find the tbody
      const selectors = [
         // '#lineitemdatails',
         '#invoice-items-body',
         '#lineItems tbody',
         '.lineitemdatails tbody',
         'table tbody', // fallback for any table tbody
         '[id*="lineitem"] tbody',
         '[class*="lineitem"] tbody',
      ];

      for (const selector of selectors) {
         const element = doc.querySelector(selector);
         if (element) {
            // Ensure we have a tbody element, not thead or the table itself
            if (element.tagName.toLowerCase() === 'tbody') {
               lineItemsBody = element;
               break;
            } else if (element.tagName.toLowerCase() === 'table') {
               // If we found a table, look for tbody inside it
               const tbody = element.querySelector('tbody');
               if (tbody) {
                  lineItemsBody = tbody;
                  break;
               } else {
                  // Create tbody if it doesn't exist, but preserve thead
                  const thead = element.querySelector('thead');
                  lineItemsBody = document.createElement('tbody');
                  element.appendChild(lineItemsBody);
                  break;
               }
            }
         }
      }

      if (!lineItemsBody) {
         console.log('Line items tbody not found - table structure may be different');
         return;
      }

      // IMPORTANT: Only clear tbody content, never touch thead
      lineItemsBody.innerHTML = '';

      // Check if we have line items array or use flat data
      const lineItems = invoice.lineitems || [invoice];

      lineItems.forEach((item, index) => {
         const row = document.createElement('tr');
         row.className = '09tax';

         // Define line item structure based on your table headers
         const lineItemFields = [
            { className: 'SrNo', value: item.srno || index + 1, type: 'text' },
            { className: 'ItemName', value: item.itemname || '', type: 'text' },
            { className: 'Qty1', value: item.qty1 || 0, type: 'text' },
            { className: 'Qty2', value: item.qty2 || 0, type: 'text' },
            { className: 'Qty3', value: item.qty3 || 0, type: 'text' },
            { className: 'Unit', value: item.unit || '', type: 'text' },
            { className: 'Rate', value: item.rate || 0, type: 'currency' },
            { className: 'ItemNetAmount', value: item.itemnetamount || 0, type: 'currency' },
            { className: 'GstPercentage', value: item.gstpercentage || 0, type: 'percentage' },
            {
               className: 'GstAmount',
               value: (item.cgstvalue || 0) + (item.sgstvalue || 0) + (item.igstvalue || 0),
               type: 'currency',
            },
            {
               className: 'TotalAmount',
               value: (item.itemnetamount || 0) + (item.cgstvalue || 0) + (item.sgstvalue || 0) + (item.igstvalue || 0),
               type: 'currency',
            },
         ];

         lineItemFields.forEach((field) => {
            const cell = document.createElement('td');
            cell.className = `09tax ${field.className}`;
            cell.textContent = formatValue(field.value, field.type, field.className);
            row.appendChild(cell);
         });

         lineItemsBody.appendChild(row);
      });
   };

   // Handle Bill Terms - Enhanced to preserve structure
   const handleBillTerms = () => {
      const billTermsBody = doc.getElementById('salebillterms');

      if (!billTermsBody || !invoice.salebillterms || !Array.isArray(invoice.salebillterms)) {
         return;
      }

      // Only clear if it's a tbody, otherwise create structure
      if (billTermsBody.tagName.toLowerCase() === 'tbody') {
         billTermsBody.innerHTML = '';
      } else {
         // If it's a table, find or create tbody
         let tbody = billTermsBody.querySelector('tbody');
         if (!tbody) {
            tbody = document.createElement('tbody');
            billTermsBody.appendChild(tbody);
         }
         tbody.innerHTML = '';
         billTermsBody = tbody;
      }

      invoice.salebillterms.forEach((term) => {
         const row = document.createElement('tr');
         row.className = '09tax';

         Object.keys(term).forEach((key) => {
            const cell = document.createElement('td');
            cell.className = '09tax';
            cell.textContent = term[key];
            row.appendChild(cell);
         });

         billTermsBody.appendChild(row);
      });
   };

   // Handle Special Elements (totals, etc.)
   const handleSpecialElements = () => {
      const taxAmount = (invoice.cgstvalue || 0) + (invoice.sgstvalue || 0) + (invoice.igstvalue || 0);
      const totalAmount = invoice.billnetamountvalue || 0;

      // Update total tax amount
      const totalTaxElements = doc.querySelectorAll('#total-tax-amount, .TotalGstAmount');
      totalTaxElements.forEach((el) => {
         el.innerHTML = `<strong>${formatCurrency(taxAmount)}</strong>`;
      });

      // Update grand total
      const grandTotalElements = doc.querySelectorAll('#grand-total, .TotalTotalAmount');
      grandTotalElements.forEach((el) => {
         el.innerHTML = `<strong>${formatCurrency(totalAmount)}</strong>`;
      });

      // Update amount in words
      const amountInWordsElements = doc.querySelectorAll('#amount-in-words, .AmountInWords');
      amountInWordsElements.forEach((el) => {
         el.textContent = numberToWords(totalAmount);
      });
   };

   // Execute all handlers
   handleLineItems();
   handleBillTerms();
   handleSpecialElements();

   return doc.documentElement.outerHTML;
}

export function getFinancialYearDates() {
   const financialYear = localStorage.getItem('selectedFinancialYear'); // e.g., "2024-2025"

   if (!financialYear || !financialYear.includes('-')) return { fromDate: '', toDate: '' };

   const [startYear, endYear] = financialYear.split('-');
   const globalFromDate = `${startYear}-04-01`; // 1 April YYYY
   const globalToDate = `${endYear}-03-31`; // 31 March YYYY

   return { globalFromDate, globalToDate };
}

export function extractModelName(input) {
   if (typeof input !== 'string') return '';
   const regex = /^(.*?)\s+\d+\s?GB\b/i;
   const match = input.match(regex);
   return match ? match[1].trim() : input.trim();
}

export function formatIndianAmount(amount) {
   let [integerPart, decimalPart] = Number(amount).toFixed(2).split('.');
   let lastThree = integerPart.slice(-3);
   let otherNumbers = integerPart.slice(0, -3);

   if (otherNumbers !== '') {
      otherNumbers = otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ',');
      integerPart = otherNumbers + ',' + lastThree;
   } else {
      integerPart = lastThree;
   }
   return `${integerPart}<span style="font-size: 0.7em;">.${decimalPart}</span>`;
}

export function truncateText(text, maxLength) {
   if (text.length <= maxLength) {
      return text;
   }
   return text.slice(0, maxLength) + '...';
}

export const formatAmountWithLabel = (amount) => {
   const numeric = Number(amount) || 0;
   const absAmount = Math.abs(numeric).toFixed(2);
   const [whole, decimal] = absAmount.split('.');
   const label = numeric < 0 ? 'DB' : 'CR';

   return (
      <span>
         ₹ {whole}
         <span style={{ fontSize: '0.7em' }}>.{decimal}</span>
      </span>
   );
};

export function formatDate(date) {
   if (!date) return null;
   const d = moment(date);
   return d.isValid() ? d.toISOString() : null;
}

export function findBySerialNo(data, serialNo, type) {
   const lineitemdatails =
      type === 'receive' ? 'receiveLineDetail' : type === 'receiveissue' ? 'issueLineDetail' : 'purchaseLineDetail';
   if (data.length === 0 || !Array.isArray(data) || data === null) return [];
   return data
      ?.map((item) => {
         if (Array.isArray(item[lineitemdatails]) && item[lineitemdatails].some((s) => s.serialNo === serialNo)) {
            return {
               ...item,
               [lineitemdatails]: item[lineitemdatails].filter((s) => s.serialNo === serialNo),
            };
         }
         return null;
      })
      .filter(Boolean);
}

/**
 * Extracts the first error message from an error object.
 *
 * @param {Record<string, string>} errors - An object with field keys and their corresponding error messages.
 * @returns {string|null} The first error message, or null if none found.
 */
export function getFirstErrorMessage(errors) {
   if (!errors || typeof errors !== 'object' || Array.isArray(errors)) {
      return null;
   }

   const errorMessages = Object.values(errors);
   return errorMessages.length > 0 ? errorMessages[0] : null;
}

/**
 * Debounce a function, ensuring it executes only after the specified delay
 * once the last invocation occurs.
 *
 * @template T
 * @param {(...args: T[]) => void} callback - The function to debounce.
 * @param {number} delay - Delay in milliseconds.
 * @returns {(...args: T[]) => void} - Debounced version of the function.
 */
export function debounce(callback, delay) {
   let timeoutId;

   return (...args) => {
      if (timeoutId) {
         clearTimeout(timeoutId);
      }

      timeoutId = setTimeout(() => {
         callback(...args);
      }, delay);
   };
}


const formatNumber = (value) => {
   if (value === undefined || value === null || value === '') return 0;
   const num = parseFloat(value);
   return isNaN(num) ? 0 : num;
};

function generateInvoiceNumber() {
   const timestamp = Date.now().toString().slice(-6);
   const random = Math.random().toString(36).substr(2, 4).toUpperCase();
   return `INV${timestamp}${random}`;
}

function generateCustomGSTRFileName(options = {}) {
   const {
      gstin = '24CIQPP5312L1ZV',
      reportType = 'GSTR1',
      month = null,
      year = null,
      includeExtension = true,
   } = options;

   const now = new Date();

   // Month handling
   const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

   const monthName = month ? monthNames[parseInt(month) - 1] : monthNames[now.getMonth()];
   const yearStr = year || now.getFullYear();

   // Current date and time
   const day = now.getDate().toString().padStart(2, '0');
   const currentMonth = (now.getMonth() + 1).toString().padStart(2, '0');
   const currentYear = now.getFullYear();
   const dateStr = `${day}${currentMonth}${currentYear}`;

   const hours = now.getHours().toString().padStart(2, '0');
   const minutes = now.getMinutes().toString().padStart(2, '0');
   const seconds = now.getSeconds().toString().padStart(2, '0');
   const timeStr = `${hours}${minutes}${seconds}`;

   const filename = `GST_${gstin}_${reportType}_${monthName}-${yearStr}_${dateStr}_${timeStr}`;

   return includeExtension ? `${filename}.xlsx` : filename;
}

function formatDateForGSTR(dateValue) {
   if (!dateValue) return new Date().toLocaleDateString('en-GB'); // dd/mm/yyyy format

   const date = new Date(dateValue);
   if (isNaN(date.getTime())) {
      return new Date().toLocaleDateString('en-GB');
   }

   // Format as dd-mm-yyyy for GSTR portal
   const day = date.getDate().toString().padStart(2, '0');
   const month = (date.getMonth() + 1).toString().padStart(2, '0');
   const year = date.getFullYear();
   return `${day}-${month}-${year}`;
}

// Main configuration object that maps your API data to Excel columns
const GSTR1_EXCEL_CONFIG = {
   // generateFileName: (gstin, month, year, reportType = 'GSTR1') => {
   //    return generateCustomGSTRFileName({ gstin, reportType, month, year, includeExtension: true });
   // },
   B2B: {
      sheetName: 'b2b,sez,de',
      startRow: 5, // Row where data starts (1-based indexing)
      summaryRow: 2, // Row for summary data
      headerRow: 4, // Row where headers are located (1-based)

      // Summary header styling (Row 1) - Blue background matching your screenshot
      summaryHeaderStyle: {
         font: {
            bold: true,
            color: { rgb: 'FFFFFF' }, // White text
            size: 11,
            name: 'Calibri',
         },
         fill: {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { rgb: '5B9BD5' }, // Blue background from your screenshot
         },
         alignment: {
            horizontal: 'center',
            vertical: 'center',
         },
         border: {
            top: { style: 'thin', color: { rgb: '000000' } },
            bottom: { style: 'thin', color: { rgb: '000000' } },
            left: { style: 'thin', color: { rgb: '000000' } },
            right: { style: 'thin', color: { rgb: '000000' } },
         },
      },

      // Summary data styling (Rows 2 & 3)
      summaryDataStyle: {
         font: {
            bold: true,
            size: 10,
            name: 'Calibri',
         },
         alignment: {
            horizontal: 'center',
            vertical: 'center',
         },
         border: {
            top: { style: 'thin', color: { rgb: '000000' } },
            bottom: { style: 'thin', color: { rgb: '000000' } },
            left: { style: 'thin', color: { rgb: '000000' } },
            right: { style: 'thin', color: { rgb: '000000' } },
         },
      },

      // Header styling (Row 4) - Same blue as summary header
      headerStyle: {
         font: {
            bold: true,
            color: { rgb: 'FFFFFF' }, // White text
            size: 10,
            name: 'Calibri',
         },
         fill: {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { rgb: '5B9BD5' }, // Same blue as summary header
         },
         alignment: {
            horizontal: 'center',
            vertical: 'center',
            wrapText: true,
         },
         border: {
            top: { style: 'thin', color: { rgb: '000000' } },
            bottom: { style: 'thin', color: { rgb: '000000' } },
            left: { style: 'thin', color: { rgb: '000000' } },
            right: { style: 'thin', color: { rgb: '000000' } },
         },
      },

      dataMapping: (apiData) => {
         return apiData.map((item) => ({
            acc_gstin: item.acc_gstin || '',
            accountname: item.accountname || '',
            // Ensure invoice number is not empty and not 0
            docnumber:
               item.docnumber && item.docnumber !== '0'
                  ? item.docnumber
                  : `INV-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
            // Ensure date is in correct format (dd-mm-yyyy)
            txndate: formatDateForGSTR(item.txndate),
            // Ensure invoice value is not 0 or negative
            netamount: Math.max(formatNumber(item.netamount) || 1, 0.01),
            placeofsupply: item.placeofsupply || '',
            reversecharge: item.reversecharge || 'N',
            // Ensure tax rate is valid (not 0 unless exempt)
            applicabletaxrate: formatNumber(item.applicabletaxrate) || 0,
            invoicetype: item.invoicetype || 'Regular B2B',
            ecom_cust_gstin: item.ecom_cust_gstin || '',
            // Ensure rate is not negative
            rate: Math.max(formatNumber(item.rate) || 0, 0),
            // Ensure taxable amount is not 0 or negative
            taxableamount: Math.max(formatNumber(item.taxableamount) || 0.01, 0.01),
            // Ensure cess is not negative
            cess: Math.max(formatNumber(item.cess) || 0, 0),
         }));
      },

      columns: [
         { key: 'acc_gstin', col: 0, header: 'GSTIN/UIN of Recipient', width: 18 },
         { key: 'accountname', col: 1, header: 'Receiver Name', width: 25 },
         { key: 'docnumber', col: 2, header: 'Invoice Number', width: 15 },
         { key: 'txndate', col: 3, header: 'Invoice date', isDate: true, width: 12 },
         { key: 'netamount', col: 4, header: 'Invoice Value', isNumber: true, width: 15 },
         { key: 'placeofsupply', col: 5, header: 'Place Of Supply', width: 15 },
         { key: 'reversecharge', col: 6, header: 'Reverse Charge', width: 12 },
         { key: 'applicabletaxrate', col: 7, header: 'Applicable % of Tax Rate', isNumber: true, width: 18 },
         { key: 'invoicetype', col: 8, header: 'Invoice Type', width: 15 },
         { key: 'ecom_cust_gstin', col: 9, header: 'E-Commerce GSTIN', width: 18 },
         { key: 'rate', col: 10, header: 'Rate', isNumber: true, width: 10 },
         { key: 'taxableamount', col: 11, header: 'Taxable Value', isNumber: true, width: 15 },
         { key: 'cess', col: 12, header: 'Cess Amount', isNumber: true, width: 12 },
      ],
   },

   B2CL: {
      sheetName: 'b2cl',
      startRow: 6,
      summaryRow: 2,
      headerRow: 5,

      // Blue header styling for B2CL
      headerStyle: {
         font: {
            bold: true,
            color: { rgb: 'FFFFFF' },
            size: 10,
            name: 'Calibri',
         },
         fill: {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { rgb: '5B9BD5' },
         },
         alignment: {
            horizontal: 'center',
            vertical: 'center',
            wrapText: true,
         },
         border: {
            top: { style: 'thin', color: { rgb: '000000' } },
            bottom: { style: 'thin', color: { rgb: '000000' } },
            left: { style: 'thin', color: { rgb: '000000' } },
            right: { style: 'thin', color: { rgb: '000000' } },
         },
      },

      summaryHeaderStyle: {
         font: {
            bold: true,
            color: { rgb: 'FFFFFF' },
            size: 11,
            name: 'Calibri',
         },
         fill: {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { rgb: '5B9BD5' },
         },
         alignment: {
            horizontal: 'center',
            vertical: 'center',
         },
         border: {
            top: { style: 'thin', color: { rgb: '000000' } },
            bottom: { style: 'thin', color: { rgb: '000000' } },
            left: { style: 'thin', color: { rgb: '000000' } },
            right: { style: 'thin', color: { rgb: '000000' } },
         },
      },

      dataMapping: (apiData) => {
         return apiData.map((item) => ({
            // Ensure invoice number is valid
            invno: item.invno || item.docnumber || generateInvoiceNumber(),
            // Ensure date is in GSTR format
            invoicedate: formatDateForGSTR(item.invoicedate || item.txndate),
            // Ensure invoice value is positive
            invoicevalue: Math.max(formatNumber(item.invoicevalue || item.netamount) || 0.01, 0.01),
            placeofsupply: item.placeofsupply || '',
            // Ensure tax rate is valid
            applicabletaxrate: formatNumber(item.applicabletaxrate) || 0,
            // Ensure rate is not negative
            rate: Math.max(formatNumber(item.rate) || 0, 0),
            // Ensure taxable amount is positive
            taxableamount: Math.max(formatNumber(item.taxableamount) || 0.01, 0.01),
            // Ensure cess is not negative
            cess: Math.max(formatNumber(item.cess) || 0, 0),
            ecom_cust_gstin: item.ecom_cust_gstin || '',
         }));
      },
      columns: [
         { key: 'invno', col: 0, header: 'Invoice Number', width: 15 },
         { key: 'invoicedate', col: 1, header: 'Invoice Date', isDate: true, width: 12 },
         { key: 'invoicevalue', col: 2, header: 'Invoice Value', isNumber: true, width: 15 },
         { key: 'placeofsupply', col: 3, header: 'Place Of Supply', width: 15 },
         { key: 'applicabletaxrate', col: 4, header: 'Applicable % of Tax Rate', isNumber: true, width: 18 },
         { key: 'rate', col: 5, header: 'Rate', isNumber: true, width: 10 },
         { key: 'taxableamount', col: 6, header: 'Taxable Value', isNumber: true, width: 15 },
         { key: 'cess', col: 7, header: 'Cess Amount', isNumber: true, width: 12 },
         { key: 'ecom_cust_gstin', col: 8, header: 'E-Commerce GSTIN', width: 18 },
      ],
   },

   B2CS: {
      sheetName: 'b2cs',
      startRow: 6,
      summaryRow: 2,
      headerRow: 5,

      // Blue header styling for B2CS
      headerStyle: {
         font: {
            bold: true,
            color: { rgb: 'FFFFFF' },
            size: 10,
            name: 'Calibri',
         },
         fill: {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { rgb: '5B9BD5' },
         },
         alignment: {
            horizontal: 'center',
            vertical: 'center',
            wrapText: true,
         },
         border: {
            top: { style: 'thin', color: { rgb: '000000' } },
            bottom: { style: 'thin', color: { rgb: '000000' } },
            left: { style: 'thin', color: { rgb: '000000' } },
            right: { style: 'thin', color: { rgb: '000000' } },
         },
      },

      summaryHeaderStyle: {
         font: {
            bold: true,
            color: { rgb: 'FFFFFF' },
            size: 11,
            name: 'Calibri',
         },
         fill: {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { rgb: '5B9BD5' },
         },
         alignment: {
            horizontal: 'center',
            vertical: 'center',
         },
         border: {
            top: { style: 'thin', color: { rgb: '000000' } },
            bottom: { style: 'thin', color: { rgb: '000000' } },
            left: { style: 'thin', color: { rgb: '000000' } },
            right: { style: 'thin', color: { rgb: '000000' } },
         },
      },

      dataMapping: (apiData) => {
         return apiData.map((item) => ({
            type: item.type || 'OE',
            placeofsupply: item.placeofsupply || '',
            // Ensure tax rate is valid
            applicabletaxrate: formatNumber(item.applicabletaxrate) || 0,
            // Ensure rate is not negative
            rate: Math.max(formatNumber(item.rate) || 0, 0),
            // Ensure taxable value is positive
            taxablevalue: Math.max(formatNumber(item.taxablevalue || item.taxableamount) || 0.01, 0.01),
            // Ensure cess is not negative
            cessamount: Math.max(formatNumber(item.cessamount || item.cess) || 0, 0),
            ecom_cust_gstin: item.ecom_cust_gstin || '',
         }));
      },
      columns: [
         { key: 'type', col: 0, header: 'Type', width: 10 },
         { key: 'placeofsupply', col: 1, header: 'Place Of Supply', width: 15 },
         { key: 'applicabletaxrate', col: 2, header: 'Applicable % Tax of Rate', isNumber: true, width: 18 },
         { key: 'rate', col: 3, header: 'Rate', isNumber: true, width: 10 },
         { key: 'taxablevalue', col: 4, header: 'Taxable Value', isNumber: true, width: 15 },
         { key: 'cessamount', col: 5, header: 'Cess Amount', isNumber: true, width: 12 },
         { key: 'ecom_cust_gstin', col: 6, header: 'E-Commerce GSTIN', width: 18 },
      ],
   },

   CDNR: {
      sheetName: 'cdnr',
      startRow: 6,
      summaryRow: 2,
      headerRow: 5,

      // Blue header styling for CDNR
      headerStyle: {
         font: {
            bold: true,
            color: { rgb: 'FFFFFF' },
            size: 10,
            name: 'Calibri',
         },
         fill: {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { rgb: '5B9BD5' },
         },
         alignment: {
            horizontal: 'center',
            vertical: 'center',
            wrapText: true,
         },
         border: {
            top: { style: 'thin', color: { rgb: '000000' } },
            bottom: { style: 'thin', color: { rgb: '000000' } },
            left: { style: 'thin', color: { rgb: '000000' } },
            right: { style: 'thin', color: { rgb: '000000' } },
         },
      },

      summaryHeaderStyle: {
         font: {
            bold: true,
            color: { rgb: 'FFFFFF' },
            size: 11,
            name: 'Calibri',
         },
         fill: {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { rgb: '5B9BD5' },
         },
         alignment: {
            horizontal: 'center',
            vertical: 'center',
         },
         border: {
            top: { style: 'thin', color: { rgb: '000000' } },
            bottom: { style: 'thin', color: { rgb: '000000' } },
            left: { style: 'thin', color: { rgb: '000000' } },
            right: { style: 'thin', color: { rgb: '000000' } },
         },
      },

      dataMapping: (apiData) => {
         return apiData.map((item) => ({
            acc_gstin: item.acc_gstin || '',
            accountname: item.accountname || '',
            // Ensure note number is valid
            docnumber: item.docnumber || generateInvoiceNumber(),
            // Ensure date is in GSTR format
            txndate: formatDateForGSTR(item.txndate),
            placeofsupply: item.placeofsupply || '',
            reversecharge: item.reversecharge || 'N',
            // Ensure tax rate is valid
            applicabletaxrate: formatNumber(item.applicabletaxrate) || 0,
            // Ensure rate is not negative
            rate: Math.max(formatNumber(item.rate) || 0, 0),
            // Ensure taxable amount is positive
            taxableamount: Math.max(formatNumber(item.taxableamount) || 0.01, 0.01),
            // Ensure cess is not negative
            cess: Math.max(formatNumber(item.cess) || 0, 0),
         }));
      },
      columns: [
         { key: 'acc_gstin', col: 0, header: 'GSTIN/UIN of Recipient', width: 18 },
         { key: 'accountname', col: 1, header: 'Receiver Name', width: 25 },
         { key: 'docnumber', col: 2, header: 'Note Number', width: 15 },
         { key: 'txndate', col: 3, header: 'Note Date', isDate: true, width: 12 },
         { key: 'placeofsupply', col: 4, header: 'Place Of Supply', width: 15 },
         { key: 'reversecharge', col: 5, header: 'Reverse Charge', width: 12 },
         { key: 'applicabletaxrate', col: 6, header: 'Applicable % of Rate', isNumber: true, width: 18 },
         { key: 'rate', col: 7, header: 'Rate', isNumber: true, width: 10 },
         { key: 'taxableamount', col: 8, header: 'Taxable Value', isNumber: true, width: 15 },
         { key: 'cess', col: 9, header: 'Cess Amount', isNumber: true, width: 12 },
      ],
   },
   // B2B: {
   //    sheetName: 'b2b,sez,de',
   //    startRow: 5, // Row where data starts (1-based indexing)
   //    summaryRow: 2, // Row for summary data
   //    headerRow: 4, // Row where headers are located (1-based)

   //    // Summary header styling (Row 1) - Blue background matching your screenshot
   //    summaryHeaderStyle: {
   //       font: {
   //          bold: true,
   //          color: { rgb: 'FFFFFF' }, // White text
   //          size: 11,
   //          name: 'Calibri',
   //       },
   //       fill: {
   //          type: 'pattern',
   //          pattern: 'solid',
   //          fgColor: { rgb: '5B9BD5' }, // Blue background from your screenshot
   //       },
   //       alignment: {
   //          horizontal: 'center',
   //          vertical: 'center',
   //       },
   //       border: {
   //          top: { style: 'thin', color: { rgb: '000000' } },
   //          bottom: { style: 'thin', color: { rgb: '000000' } },
   //          left: { style: 'thin', color: { rgb: '000000' } },
   //          right: { style: 'thin', color: { rgb: '000000' } },
   //       },
   //    },

   //    // Summary data styling (Rows 2 & 3)
   //    summaryDataStyle: {
   //       font: {
   //          bold: true,
   //          size: 10,
   //          name: 'Calibri',
   //       },
   //       alignment: {
   //          horizontal: 'center',
   //          vertical: 'center',
   //       },
   //       border: {
   //          top: { style: 'thin', color: { rgb: '000000' } },
   //          bottom: { style: 'thin', color: { rgb: '000000' } },
   //          left: { style: 'thin', color: { rgb: '000000' } },
   //          right: { style: 'thin', color: { rgb: '000000' } },
   //       },
   //    },

   //    // Header styling (Row 4) - Same blue as summary header
   //    headerStyle: {
   //       font: {
   //          bold: true,
   //          color: { rgb: 'FFFFFF' }, // White text
   //          size: 10,
   //          name: 'Calibri',
   //       },
   //       fill: {
   //          type: 'pattern',
   //          pattern: 'solid',
   //          fgColor: { rgb: '5B9BD5' }, // Same blue as summary header
   //       },
   //       alignment: {
   //          horizontal: 'center',
   //          vertical: 'center',
   //          wrapText: true,
   //       },
   //       border: {
   //          top: { style: 'thin', color: { rgb: '000000' } },
   //          bottom: { style: 'thin', color: { rgb: '000000' } },
   //          left: { style: 'thin', color: { rgb: '000000' } },
   //          right: { style: 'thin', color: { rgb: '000000' } },
   //       },
   //    },

   //    dataMapping: (apiData) => {
   //       return apiData.map((item) => ({
   //          acc_gstin: item.acc_gstin || '',
   //          accountname: item.accountname || '',
   //          docnumber: item.docnumber || '',
   //          txndate: formatDate(item.txndate),
   //          netamount: formatNumber(item.netamount),
   //          placeofsupply: item.placeofsupply || '',
   //          reversecharge: item.reversecharge || 'N',
   //          applicabletaxrate: formatNumber(item.applicabletaxrate),
   //          invoicetype: item.invoicetype || 'Regular B2B',
   //          ecom_cust_gstin: item.ecom_cust_gstin || '',
   //          rate: formatNumber(item.rate) || 0.0,
   //          taxableamount: formatNumber(item.taxableamount),
   //          cess: formatNumber(item.cess),
   //       }));
   //    },

   //    columns: [
   //       { key: 'acc_gstin', col: 0, header: 'GSTIN/UIN of Recipient', width: 18 },
   //       { key: 'accountname', col: 1, header: 'Receiver Name', width: 25 },
   //       { key: 'docnumber', col: 2, header: 'Invoice Number', width: 15 },
   //       { key: 'txndate', col: 3, header: 'Invoice date', isDate: true, width: 12 },
   //       { key: 'netamount', col: 4, header: 'Invoice Value', isNumber: true, width: 15 },
   //       { key: 'placeofsupply', col: 5, header: 'Place Of Supply', width: 15 },
   //       { key: 'reversecharge', col: 6, header: 'Reverse Charge', width: 12 },
   //       { key: 'applicabletaxrate', col: 7, header: 'Applicable % of Tax Rate', isNumber: true, width: 18 },
   //       { key: 'invoicetype', col: 8, header: 'Invoice Type', width: 15 },
   //       { key: 'ecom_cust_gstin', col: 9, header: 'E-Commerce GSTIN', width: 18 },
   //       { key: 'rate', col: 10, header: 'Rate', isNumber: true, width: 10 },
   //       { key: 'taxableamount', col: 11, header: 'Taxable Value', isNumber: true, width: 15 },
   //       { key: 'cess', col: 12, header: 'Cess Amount', isNumber: true, width: 12 },
   //    ],
   // },
   // B2B: {
   //    sheetName: 'b2b,sez,de',
   //    startRow: 5, // Row where data starts (1-based indexing)
   //    summaryRow: 2, // Row for summary data
   //    headerRow: 4, // Row where headers are located (1-based)

   //    // Summary header styling (Row 1) - Blue background matching your screenshot
   //    summaryHeaderStyle: {
   //       font: {
   //          bold: true,
   //          color: { rgb: 'FFFFFF' }, // White text
   //          size: 11,
   //          name: 'Calibri',
   //       },
   //       fill: {
   //          type: 'pattern',
   //          pattern: 'solid',
   //          fgColor: { rgb: '5B9BD5' }, // Exact blue from your screenshot
   //       },
   //       alignment: {
   //          horizontal: 'center',
   //          vertical: 'center',
   //       },
   //       border: {
   //          top: { style: 'thin', color: { rgb: '000000' } },
   //          bottom: { style: 'thin', color: { rgb: '000000' } },
   //          left: { style: 'thin', color: { rgb: '000000' } },
   //          right: { style: 'thin', color: { rgb: '000000' } },
   //       },
   //    },

   //    // Summary data styling (Rows 2 & 3)
   //    summaryDataStyle: {
   //       font: {
   //          bold: true,
   //          size: 10,
   //          name: 'Calibri',
   //       },
   //       alignment: {
   //          horizontal: 'center',
   //          vertical: 'center',
   //       },
   //       border: {
   //          top: { style: 'thin', color: { rgb: '000000' } },
   //          bottom: { style: 'thin', color: { rgb: '000000' } },
   //          left: { style: 'thin', color: { rgb: '000000' } },
   //          right: { style: 'thin', color: { rgb: '000000' } },
   //       },
   //    },

   //    // Header styling (Row 4) - Light orange/tan background as shown
   //    headerStyle: {
   //       font: {
   //          bold: true,
   //          color: { rgb: '000000' }, // Black text
   //          size: 10,
   //          name: 'Calibri',
   //       },
   //       fill: {
   //          type: 'pattern',
   //          pattern: 'solid',
   //          fgColor: { rgb: 'F2CC8F' }, // Light orange/tan background from screenshot
   //       },
   //       alignment: {
   //          horizontal: 'center',
   //          vertical: 'center',
   //          wrapText: true,
   //       },
   //       border: {
   //          top: { style: 'thin', color: { rgb: '000000' } },
   //          bottom: { style: 'thin', color: { rgb: '000000' } },
   //          left: { style: 'thin', color: { rgb: '000000' } },
   //          right: { style: 'thin', color: { rgb: '000000' } },
   //       },
   //    },

   //    dataMapping: (apiData) => {
   //       return apiData.map((item) => ({
   //          acc_gstin: item.acc_gstin || '',
   //          accountname: item.accountname || '',
   //          docnumber: item.docnumber || '',
   //          txndate: formatDate(item.txndate),
   //          netamount: formatNumber(item.netamount),
   //          placeofsupply: item.placeofsupply || '',
   //          reversecharge: item.reversecharge || 'N',
   //          applicabletaxrate: formatNumber(item.applicabletaxrate),
   //          invoicetype: item.invoicetype || 'Regular B2B',
   //          ecom_cust_gstin: item.ecom_cust_gstin || '',
   //          rate: formatNumber(item.rate) || 0.0,
   //          taxableamount: formatNumber(item.taxableamount),
   //          cess: formatNumber(item.cess),
   //       }));
   //    },

   //    columns: [
   //       { key: 'acc_gstin', col: 0, header: 'GSTIN/UIN of Recipient', width: 18 },
   //       { key: 'accountname', col: 1, header: 'Receiver Name', width: 25 },
   //       { key: 'docnumber', col: 2, header: 'Invoice Number', width: 15 },
   //       { key: 'txndate', col: 3, header: 'Invoice date', isDate: true, width: 12 },
   //       { key: 'netamount', col: 4, header: 'Invoice Value', isNumber: true, width: 15 },
   //       { key: 'placeofsupply', col: 5, header: 'Place Of Supply', width: 15 },
   //       { key: 'reversecharge', col: 6, header: 'Reverse Charge', width: 12 },
   //       { key: 'applicabletaxrate', col: 7, header: 'Applicable % of Tax Rate', isNumber: true, width: 18 },
   //       { key: 'invoicetype', col: 8, header: 'Invoice Type', width: 15 },
   //       { key: 'ecom_cust_gstin', col: 9, header: 'E-Commerce GSTIN', width: 18 },
   //       { key: 'rate', col: 10, header: 'Rate', isNumber: true, width: 10 },
   //       { key: 'taxableamount', col: 11, header: 'Taxable Value', isNumber: true, width: 15 },
   //       { key: 'cess', col: 12, header: 'Cess Amount', isNumber: true, width: 12 },
   //    ],
   // },
   // B2B: {
   //    sheetName: 'b2b,sez,de',
   //    startRow: 5, // Row where data starts (1-based indexing)
   //    summaryRow: 2, // Row for summary data
   //    headerRow: 4, // Row where headers are located (1-based)

   //    // Header styling to match your screenshot - Blue background with white text
   //    headerStyle: {
   //       font: {
   //          bold: true,
   //          color: { rgb: 'FFFFFF' }, // White text
   //          size: 11,
   //          name: 'Calibri',
   //       },
   //       fill: {
   //          type: 'pattern',
   //          pattern: 'solid',
   //          fgColor: { rgb: '4472C4' }, // Blue background matching your screenshot
   //       },
   //       alignment: {
   //          horizontal: 'center',
   //          vertical: 'center',
   //          wrapText: true,
   //       },
   //       border: {
   //          top: { style: 'thin', color: { rgb: '000000' } },
   //          bottom: { style: 'thin', color: { rgb: '000000' } },
   //          left: { style: 'thin', color: { rgb: '000000' } },
   //          right: { style: 'thin', color: { rgb: '000000' } },
   //       },
   //    },

   //    // Summary row styling (row 1 - blue background)
   //    summaryHeaderStyle: {
   //       font: {
   //          bold: true,
   //          color: { rgb: 'FFFFFF' },
   //          size: 11,
   //          name: 'Calibri',
   //       },
   //       fill: {
   //          type: 'pattern',
   //          pattern: 'solid',
   //          fgColor: { rgb: '4472C4' },
   //       },
   //       alignment: {
   //          horizontal: 'center',
   //          vertical: 'center',
   //       },
   //       border: {
   //          top: { style: 'thin', color: { rgb: '000000' } },
   //          bottom: { style: 'thin', color: { rgb: '000000' } },
   //          left: { style: 'thin', color: { rgb: '000000' } },
   //          right: { style: 'thin', color: { rgb: '000000' } },
   //       },
   //    },

   //    dataMapping: (apiData) => {
   //       return apiData.map((item) => ({
   //          // Columns A-G (basic invoice details)
   //          acc_gstin: item.acc_gstin || '',
   //          accountname: item.accountname || '',
   //          docnumber: item.docnumber || '',
   //          txndate: formatDate(item.txndate),
   //          netamount: formatNumber(item.netamount),
   //          placeofsupply: item.placeofsupply || '',
   //          reversecharge: item.reversecharge || 'N',

   //          // Columns H-O (revised details)
   //          applicabletaxrate: formatNumber(item.applicabletaxrate),
   //          invoicetype: item.invoicetype || 'Regular B2B',
   //          ecom_cust_gstin: item.ecom_cust_gstin || '',
   //          rate: formatNumber(item.rate) || 0.0, // Default rate
   //          taxableamount: formatNumber(item.taxableamount),
   //          cess: formatNumber(item.cess),
   //       }));
   //    },

   //    columns: [
   //       // Main columns as per your screenshot
   //       { key: 'acc_gstin', col: 0, header: 'GSTIN/UIN of Recipient', width: 20 },
   //       { key: 'accountname', col: 1, header: 'Receiver Name', width: 25 },
   //       { key: 'docnumber', col: 2, header: 'Invoice Number', width: 15 },
   //       { key: 'txndate', col: 3, header: 'Invoice date', isDate: true, width: 12 },
   //       { key: 'netamount', col: 4, header: 'Invoice Value', isNumber: true, width: 15 },
   //       { key: 'placeofsupply', col: 5, header: 'Place Of Supply', width: 15 },
   //       { key: 'reversecharge', col: 6, header: 'Reverse Charge', width: 12 },
   //       { key: 'applicabletaxrate', col: 7, header: 'Applicable % of Tax Rate', isNumber: true, width: 18 },
   //       { key: 'invoicetype', col: 8, header: 'Invoice Type', width: 15 },
   //       { key: 'ecom_cust_gstin', col: 9, header: 'E-Commerce GSTIN', width: 18 },
   //       { key: 'rate', col: 10, header: 'Rate', isNumber: true, width: 10 },
   //       { key: 'taxableamount', col: 11, header: 'Taxable Value', isNumber: true, width: 15 },
   //       { key: 'cess', col: 12, header: 'Cess Amount', isNumber: true, width: 12 },
   //    ],
   // },
   // B2B: {
   //    sheetName: 'b2b,sez,de',
   //    startRow: 5, // Row where data starts (1-based indexing)
   //    summaryRow: 2, // Row for summary data
   //    dataMapping: (apiData) => {
   //       return apiData.map((item) => ({
   //          // Columns A-G (basic invoice details)
   //          acc_gstin: item.acc_gstin || '',
   //          accountname: item.accountname || '',
   //          docnumber: item.docnumber || '',
   //          txndate: formatDate(item.txndate),
   //          netamount: formatNumber(item.netamount),
   //          placeofsupply: item.placeofsupply || '',
   //          reversecharge: item.reversecharge || 'N',

   //          // Columns H-O (revised details as shown in your screenshot)
   //          placeofsupply_h: item.placeofsupply || '', // Column H
   //          reversecharge_i: item.reversecharge || 'N', // Column I
   //          applicabletaxrate: formatNumber(item.applicabletaxrate), // Column J
   //          invoicetype: item.invoicetype || 'Regular B2B', // Column K
   //          ecom_cust_gstin: item.ecom_cust_gstin || '', // Column L
   //          rate: formatNumber(item.rate), // Column M
   //          taxableamount: formatNumber(item.taxableamount), // Column N
   //          cess: formatNumber(item.cess), // Column O
   //       }));
   //    },
   //    columns: [
   //       // Basic columns (A-G)
   //       { key: 'acc_gstin', col: 0, header: 'GSTIN/UIN of Recipient' },
   //       { key: 'accountname', col: 1, header: 'Receiver Name' },
   //       { key: 'docnumber', col: 2, header: 'Invoice Number' },
   //       { key: 'txndate', col: 3, header: 'Invoice Date', isDate: true },
   //       { key: 'netamount', col: 4, header: 'Invoice Value', isNumber: true },
   //       { key: 'placeofsupply', col: 5, header: 'Place Of Supply' },
   //       { key: 'reversecharge', col: 6, header: 'Reverse Charge' },

   //       // Revised details columns (H-O)
   //       { key: 'placeofsupply_h', col: 7, header: 'Place Of Supply' },
   //       { key: 'reversecharge_i', col: 8, header: 'Reverse Charge' },
   //       { key: 'applicabletaxrate', col: 9, header: 'Applicable % of Tax Rate', isNumber: true },
   //       { key: 'invoicetype', col: 10, header: 'Invoice Type' },
   //       { key: 'ecom_cust_gstin', col: 11, header: 'E-Commerce GSTIN' },
   //       { key: 'rate', col: 12, header: 'Rate', isNumber: true },
   //       { key: 'taxableamount', col: 13, header: 'Taxable Value', isNumber: true },
   //       { key: 'cess', col: 14, header: 'Cess Amount', isNumber: true },
   //    ],
   // },

   // B2CL: {
   //    sheetName: 'b2cl',
   //    startRow: 6,
   //    summaryRow: 2,
   //    dataMapping: (apiData) => {
   //       return apiData.map((item) => ({
   //          invno: item.invno || item.docnumber || '',
   //          invoicedate: formatDate(item.invoicedate || item.txndate),
   //          invoicevalue: formatNumber(item.invoicevalue || item.netamount),
   //          placeofsupply: item.placeofsupply || '',
   //          applicabletaxrate: formatNumber(item.applicabletaxrate),
   //          rate: formatNumber(item.rate),
   //          taxableamount: formatNumber(item.taxableamount),
   //          cess: formatNumber(item.cess),
   //          ecom_cust_gstin: item.ecom_cust_gstin || '',
   //       }));
   //    },
   //    columns: [
   //       { key: 'invno', col: 0, header: 'Invoice Number' },
   //       { key: 'invoicedate', col: 1, header: 'Invoice Date', isDate: true },
   //       { key: 'invoicevalue', col: 2, header: 'Invoice Value', isNumber: true },
   //       { key: 'placeofsupply', col: 3, header: 'Place Of Supply' },
   //       { key: 'applicabletaxrate', col: 4, header: 'Applicable % of Tax Rate', isNumber: true },
   //       { key: 'rate', col: 5, header: 'Rate', isNumber: true },
   //       { key: 'taxableamount', col: 6, header: 'Taxable Value', isNumber: true },
   //       { key: 'cess', col: 7, header: 'Cess Amount', isNumber: true },
   //       { key: 'ecom_cust_gstin', col: 8, header: 'E-Commerce GSTIN' },
   //    ],
   // },

   // B2CS: {
   //    sheetName: 'b2cs',
   //    startRow: 6,
   //    summaryRow: 2,
   //    dataMapping: (apiData) => {
   //       return apiData.map((item) => ({
   //          type: item.type || 'OE',
   //          placeofsupply: item.placeofsupply || '',
   //          applicabletaxrate: formatNumber(item.applicabletaxrate),
   //          rate: formatNumber(item.rate),
   //          taxablevalue: formatNumber(item.taxablevalue || item.taxableamount),
   //          cessamount: formatNumber(item.cessamount || item.cess),
   //          ecom_cust_gstin: item.ecom_cust_gstin || '',
   //       }));
   //    },
   //    columns: [
   //       { key: 'type', col: 0, header: 'Type' },
   //       { key: 'placeofsupply', col: 1, header: 'Place Of Supply' },
   //       { key: 'applicabletaxrate', col: 2, header: 'Applicable % Tax of Rate', isNumber: true },
   //       { key: 'rate', col: 3, header: 'Rate', isNumber: true },
   //       { key: 'taxablevalue', col: 4, header: 'Taxable Value', isNumber: true },
   //       { key: 'cessamount', col: 5, header: 'Cess Amount', isNumber: true },
   //       { key: 'ecom_cust_gstin', col: 6, header: 'E-Commerce GSTIN' },
   //    ],
   // },

   // CDNR: {
   //    sheetName: 'cdnr',
   //    startRow: 6,
   //    summaryRow: 2,
   //    dataMapping: (apiData) => {
   //       return apiData.map((item) => ({
   //          acc_gstin: item.acc_gstin || '',
   //          accountname: item.accountname || '',
   //          docnumber: item.docnumber || '',
   //          txndate: formatDate(item.txndate),
   //          placeofsupply: item.placeofsupply || '',
   //          reversecharge: item.reversecharge || 'N',
   //          applicabletaxrate: formatNumber(item.applicabletaxrate),
   //          rate: formatNumber(item.rate),
   //          taxableamount: formatNumber(item.taxableamount),
   //          cess: formatNumber(item.cess),
   //       }));
   //    },
   //    columns: [
   //       { key: 'acc_gstin', col: 0, header: 'GSTIN/UIN of Recipient' },
   //       { key: 'accountname', col: 1, header: 'Receiver Name' },
   //       { key: 'docnumber', col: 2, header: 'Note Number' },
   //       { key: 'txndate', col: 3, header: 'Note Date', isDate: true },
   //       { key: 'placeofsupply', col: 4, header: 'Place Of Supply' },
   //       { key: 'reversecharge', col: 5, header: 'Reverse Charge' },
   //       { key: 'applicabletaxrate', col: 6, header: 'Applicable % of Rate', isNumber: true },
   //       { key: 'rate', col: 7, header: 'Rate', isNumber: true },
   //       { key: 'taxableamount', col: 8, header: 'Taxable Value', isNumber: true },
   //       { key: 'cess', col: 9, header: 'Cess Amount', isNumber: true },
   //    ],
   // },
};

export const downloadGSTRExcelFile = (fileName, filePath) => async (dispatch) => {
   dispatch({ type: GSTR_EXCEL_DOWNLOAD_START });

   try {
      const res = await axiosInstance.get(`/gstr-excel/download/${fileName}`, {
         params: { filePath },
         responseType: 'blob',
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      dispatch({ type: GSTR_EXCEL_DOWNLOAD_SUCCESS });
      toast.success('File downloaded successfully');
   } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Error downloading file';

      dispatch({
         type: GSTR_EXCEL_DOWNLOAD_ERROR,
         payload: errorMessage,
      });

      if (err.response?.status === 404) {
         toast.error('File not found');
      } else if (err.response?.status === 500) {
         toast.error('Issue From Server');
      } else {
         toast.error(errorMessage);
      }
      throw err;
   }
};

//working with frontend
const exportGSTR1ToExcel = async (reportType, apiData, dateRange = {}) => {
   if (!apiData || apiData.length === 0) {
      alert('No data available to download');
      return;
   }

   const config = GSTR1_EXCEL_CONFIG[reportType];
   if (!config) {
      alert(`Configuration not found for ${reportType}`);
      return;
   }

   try {
      // Load the GSTR1 template
      const response = await fetch('/GSTR1.xlsx');
      if (!response.ok) {
         throw new Error('Failed to fetch GSTR1.xlsx template file');
      }

      const arrayBuffer = await response.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array', cellStyles: true });

      // Get or create the worksheet
      let worksheet;
      if (workbook.Sheets[config.sheetName]) {
         worksheet = workbook.Sheets[config.sheetName];
      } else {
         worksheet = XLSX.utils.aoa_to_sheet([]);
         workbook.Sheets[config.sheetName] = worksheet;
         workbook.SheetNames.unshift(config.sheetName);
      }

      // Map API data
      const mappedData = config.dataMapping(apiData);

      // Clear existing data (preserve template structure)
      const existingRange = worksheet['!ref'];
      if (existingRange) {
         const range = XLSX.utils.decode_range(existingRange);
         for (let row = config.startRow - 1; row <= range.e.r; row++) {
            for (let col = 0; col <= range.e.c; col++) {
               const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
               if (worksheet[cellAddress] && row >= config.startRow - 1) {
                  delete worksheet[cellAddress];
               }
            }
         }
      }

      // Row 1: Summary header "Summary For B2B, SEZ, DE (4A, 4B, 6C)" in cells A1:B1
      const summaryHeaderText = `Summary For B2B, SEZ, DE (4A, 4B, 6C)`;
      const summaryHeaderCell = XLSX.utils.encode_cell({ r: 0, c: 0 });
      worksheet[summaryHeaderCell] = {
         v: summaryHeaderText,
         t: 's',
         s: config.summaryHeaderStyle,
      };

      // Initialize merges array if it doesn't exist
      if (!worksheet['!merges']) worksheet['!merges'] = [];

      // Merge cells A1:B1 for summary header
      worksheet['!merges'].push({
         s: { r: 0, c: 0 },
         e: { r: 0, c: 1 },
      });

      // Row 2: Summary labels in specific cells as per your screenshot
      // "No. of Recipients" in A2
      worksheet[XLSX.utils.encode_cell({ r: 1, c: 0 })] = {
         v: 'No. of Recipients',
         t: 's',
         s: config.summaryDataStyle,
      };

      // "No. of Invoices" in C2
      worksheet[XLSX.utils.encode_cell({ r: 1, c: 2 })] = {
         v: 'No. of Invoices',
         t: 's',
         s: config.summaryDataStyle,
      };

      // "Total Invoice Value" in E2
      worksheet[XLSX.utils.encode_cell({ r: 1, c: 4 })] = {
         v: 'Total Invoice Value',
         t: 's',
         s: config.summaryDataStyle,
      };

      // "Total Taxable Value" in L2
      worksheet[XLSX.utils.encode_cell({ r: 1, c: 11 })] = {
         v: 'Total Taxable Value',
         t: 's',
         s: config.summaryDataStyle,
      };

      // "Total Cess" in M2
      worksheet[XLSX.utils.encode_cell({ r: 1, c: 12 })] = {
         v: 'Total Cess',
         t: 's',
         s: config.summaryDataStyle,
      };

      // Row 3: Summary values in corresponding cells
      const totalRecords = mappedData.length;
      const totalInvoices = mappedData.length;
      const totalInvoiceValue = mappedData.reduce((sum, item) => sum + (parseFloat(item.netamount) || 0), 0);
      const totalTaxableValue = mappedData.reduce((sum, item) => sum + (parseFloat(item.taxableamount) || 0), 0);
      const totalCess = mappedData.reduce((sum, item) => sum + (parseFloat(item.cess) || 0), 0);

      // "Total Recipients" value in A3
      worksheet[XLSX.utils.encode_cell({ r: 2, c: 0 })] = {
         v: totalRecords,
         t: 'n',
         s: config.summaryDataStyle,
      };

      // "Total Invoices" value in C3
      worksheet[XLSX.utils.encode_cell({ r: 2, c: 2 })] = {
         v: totalInvoices,
         t: 'n',
         s: config.summaryDataStyle,
      };

      // "Total Invoice Value" value in E3
      worksheet[XLSX.utils.encode_cell({ r: 2, c: 4 })] = {
         v: totalInvoiceValue,
         t: 'n',
         s: {
            ...config.summaryDataStyle,
            numFmt: '#,##0.00',
         },
      };

      // "Total Taxable Value" value in L3
      worksheet[XLSX.utils.encode_cell({ r: 2, c: 11 })] = {
         v: totalTaxableValue,
         t: 'n',
         s: {
            ...config.summaryDataStyle,
            numFmt: '#,##0.00',
         },
      };

      // "Total Cess" value in M3
      worksheet[XLSX.utils.encode_cell({ r: 2, c: 12 })] = {
         v: totalCess,
         t: 'n',
         s: {
            ...config.summaryDataStyle,
            numFmt: '#,##0.00',
         },
      };

      // Add black borders to all remaining cells in rows 1-3 that don't have content
      for (let row = 0; row < 3; row++) {
         for (let col = 0; col < config.columns.length; col++) {
            const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
            if (!worksheet[cellAddress]) {
               worksheet[cellAddress] = {
                  v: '',
                  t: 's',
                  s: {
                     border: {
                        top: { style: 'thin', color: { rgb: '000000' } },
                        bottom: { style: 'thin', color: { rgb: '000000' } },
                        left: { style: 'thin', color: { rgb: '000000' } },
                        right: { style: 'thin', color: { rgb: '000000' } },
                     },
                  },
               };
            }
         }
      }

      // Row 4: Column headers with light orange background
      config.columns.forEach((colConfig, index) => {
         const headerCellAddress = XLSX.utils.encode_cell({
            r: config.headerRow - 1,
            c: colConfig.col,
         });

         worksheet[headerCellAddress] = {
            v: colConfig.header,
            t: 's',
            s: config.headerStyle,
         };
      });

      // Data cell styles
      const dataCellStyles = {
         number: {
            numFmt: '#,##0.00',
            alignment: { horizontal: 'right', vertical: 'center' },
            border: {
               top: { style: 'thin', color: { rgb: '000000' } },
               bottom: { style: 'thin', color: { rgb: '000000' } },
               left: { style: 'thin', color: { rgb: '000000' } },
               right: { style: 'thin', color: { rgb: '000000' } },
            },
         },
         date: {
            numFmt: 'dd-mm-yyyy',
            alignment: { horizontal: 'center', vertical: 'center' },
            border: {
               top: { style: 'thin', color: { rgb: '000000' } },
               bottom: { style: 'thin', color: { rgb: '000000' } },
               left: { style: 'thin', color: { rgb: '000000' } },
               right: { style: 'thin', color: { rgb: '000000' } },
            },
         },
         text: {
            alignment: { horizontal: 'left', vertical: 'center' },
            border: {
               top: { style: 'thin', color: { rgb: '000000' } },
               bottom: { style: 'thin', color: { rgb: '000000' } },
               left: { style: 'thin', color: { rgb: '000000' } },
               right: { style: 'thin', color: { rgb: '000000' } },
            },
         },
      };

      // Populate data starting from row 5
      mappedData.forEach((row, rowIndex) => {
         const actualRow = config.startRow - 1 + rowIndex;

         config.columns.forEach((colConfig) => {
            const cellAddress = XLSX.utils.encode_cell({
               r: actualRow,
               c: colConfig.col,
            });

            let value = row[colConfig.key];
            let cellType = 's';
            let style = dataCellStyles.text;

            // Handle different data types
            if (colConfig.isDate && value) {
               if (value instanceof Date) {
                  cellType = 'd';
                  style = dataCellStyles.date;
               } else {
                  const date = new Date(value);
                  if (!isNaN(date.getTime())) {
                     value = date;
                     cellType = 'd';
                     style = dataCellStyles.date;
                  }
               }
            } else if (colConfig.isNumber && value !== undefined && value !== '') {
               const numValue = parseFloat(value);
               if (!isNaN(numValue)) {
                  value = numValue;
                  cellType = 'n';
                  style = dataCellStyles.number;
               }
            }

            worksheet[cellAddress] = {
               v: value || '',
               t: cellType,
               s: style,
            };
         });
      });

      // Set column widths
      const columnWidths = config.columns.map((col) => ({
         wpx: (col.width || 15) * 7,
      }));
      worksheet['!cols'] = columnWidths;

      // Update worksheet range
      const maxRow = Math.max(config.startRow - 1 + mappedData.length - 1, config.headerRow - 1);
      const maxCol = Math.max(...config.columns.map((col) => col.col));

      worksheet['!ref'] = XLSX.utils.encode_range({
         s: { r: 0, c: 0 },
         e: { r: maxRow, c: maxCol },
      });

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0];
      const fileName = `GSTR1_${reportType}_${dateRange.fromDate || 'latest'}_to_${dateRange.toDate || 'latest'
         }_${timestamp}.xlsx`;

      // Write workbook with all styling preserved
      const buffer = XLSX.write(workbook, {
         type: 'array',
         bookType: 'xlsx',
         cellStyles: true,
         cellFormula: true,
         cellDates: true,
         sheetStubs: false,
      });

      // Download the file
      const blob = new Blob([buffer], {
         type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      console.log(`Excel file "${fileName}" downloaded successfully!`);
      alert(`Excel file "${fileName}" downloaded successfully with proper formatting!`);
   } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert(`Error exporting to Excel: ${error.message}`);
   }
};

export async function exportGSTR1Excel() {
   console.log('============ 👉 EXCEL EXPORT START 👈 ===============');

   console.log('============ 👉 EXCEL EXPORT START 👈 ===============');
}

// Function to export all GSTR1 sheets at once
const exportAllGSTR1Sheets = async (allReportsData, dateRange = {}) => {
   try {
      const response = await fetch('/GSTR1.xlsx');
      if (!response.ok) {
         throw new Error('Failed to fetch GSTR1.xlsx template file');
      }

      const arrayBuffer = await response.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array', cellStyles: true });

      const processedSheets = [];

      // Process each report type that has data
      Object.entries(allReportsData).forEach(([reportType, data]) => {
         if (!data || data.length === 0) return;

         const config = GSTR1_EXCEL_CONFIG[reportType];
         if (!config) return;

         try {
            let worksheet;
            if (workbook.SheetNames.includes(config.sheetName)) {
               worksheet = workbook.Sheets[config.sheetName];
            } else {
               worksheet = XLSX.utils.aoa_to_sheet([]);
               workbook.SheetNames.push(config.sheetName);
               workbook.Sheets[config.sheetName] = worksheet;
            }

            // Process data for this sheet
            const mappedData = config.dataMapping(data);

            // Clear and populate data (similar to single sheet export)
            // ... (implementation similar to exportGSTR1ToExcel)

            processedSheets.push(reportType);
         } catch (error) {
            console.error(`Error processing ${reportType}:`, error);
         }
      });

      if (processedSheets.length === 0) {
         alert('No data found to export');
         return;
      }

      // Download complete file
      const fileName = `GSTR1_Complete_${dateRange.fromDate || 'latest'}_to_${dateRange.toDate || 'latest'}_${new Date().toISOString().split('T')[0]
         }.xlsx`;

      const buffer = XLSX.write(workbook, {
         type: 'array',
         bookType: 'xlsx',
         cellStyles: true,
      });

      const blob = new Blob([buffer], {
         type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      alert(`Complete GSTR1 Excel with ${processedSheets.length} sheets downloaded successfully!`);
   } catch (error) {
      console.error('Error exporting complete GSTR1:', error);
      alert(`Error: ${error.message}`);
   }
};

const downloadBase64File = (base64Data, fileName) => {
   try {
      // Convert base64 to blob
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
         byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {
         type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
   } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Error downloading file');
   }
};

export {
   GSTR1_EXCEL_CONFIG,
   exportGSTR1ToExcel,
   exportAllGSTR1Sheets,
   downloadBase64File,
   // formatDate,
   formatNumber,
};

export function hasPurchase(companies, purchaseId) {
   if (!Array.isArray(companies) || companies.length === 0) {
      return false;
   }

   for (const company of companies) {
      if (!Array.isArray(company.data)) continue;

      for (const item of company.data) {
         if (!Array.isArray(item.data)) continue;

         if (item.data.some((entry) => entry.purchaseHeaderId === purchaseId)) {
            return true;
         }
      }
   }

   return false;
}

export function getFinancialYear(dateStr) {
   if (!/^\d{4}-\d{4}$/.test(dateStr)) {
      return null;
   }

   const [startYear, endYear] = dateStr.split('-').map(Number);
   if (endYear !== startYear + 1) {
      return null;
   }

   return {
      financialYear: `${startYear}-${endYear}`,
      startDate: `${startYear}-04-01`, // 1st April of start year
      endDate: `${endYear}-03-31`, // 31st March of next year
   };
}

export const getReturnStatus = (rowData, type) => {
   if (type === 'receive') return rowData?.lineitems?.[0]?.receiveLineDetail?.[0]?.returnStatus;
   return rowData?.lineitems?.[0]?.issueLineDetail?.[0]?.returnStatus;
};

export function getCurrentFinancialYear() {
   const date = moment();
   const year = date.month() >= 3 ? date.year() : date.year() - 1;
   return `${year}-${year + 1}`;
}

export function isElementDisabled(element) {
   if (!element) return true;

   return (
      element.disabled ||
      element.getAttribute('disabled') !== null ||
      element.classList.contains('disabled') ||
      element.getAttribute('aria-disabled') === 'true'
   );
}

export function forceDisabledCheck(element) {
   if (!element) return true;

   void element.offsetHeight;

   const style = window.getComputedStyle(element);
   const isStyleDisabled = style.pointerEvents === 'none' || style.opacity === '0.5';

   return element.disabled || isStyleDisabled;
}

export function getGstRate(itemRate, rows, header, index) {
   const rate = Number(itemRate || 0);
   const gstPercent = Number(rows[header][index].gst || 0);
   const gstRate = rate * (1 + gstPercent / 100);
   return gstRate;
}

export const filterRoutes = (routes, user, setting) => {
   return routes.map(route => {
      const pkg = user?.packageData?.[0];
      const admin = user?.isAdmin;
      const rights = user?.userRights?.find((ur) => ur?.companyId === user?.loginhistory?.companyId);
      const businessType = localStorage.getItem('businessTypes');
      const mobile = businessType === 'MOBILE';

      if (route.type === "separator" || !route.name) return route;

      if (route.name === "Dashboard") {
         const dashboard = (admin || rights?.dashboard?.view);
         if (!dashboard) {
            return null;
         }
         return route;
      }
      if (route.name === "Masters") {
         const masters = (admin || (rights?.customer?.view || rights?.vendor?.view || rights?.allAccount?.view || rights?.item?.view || rights?.category?.view || rights?.brand?.view || rights?.salerepresentative?.view || rights?.itemGroup?.view || rights?.warehouse?.view || rights?.hsn?.view || rights?.unit?.view || rights?.daybook?.view || rights?.billtterm?.view || rights?.bankbranch?.view || rights?.transport?.view || rights?.driver?.view || rights?.vehicle?.view || rights?.color?.view || rights?.style?.view || rights?.size?.view || rights?.country?.view || rights?.states?.view || rights?.city?.view || rights?.area?.view));
         if (!masters) {
            return null;
         }
         return route;
      }
      if (route.name === "Reports") {
         const reports = (admin || rights?.reports?.view);
         if (!reports) {
            return null;
         }
         return route;
      }
      if (route.name === "Sales") {
         const salesInvoice = (pkg?.sales_invoice || false) && (admin || rights?.saleinvoice?.view);
         const salesReturn = (pkg?.sales_return || false) && (admin || rights?.salereturn?.view);
         const salesChallan = ((pkg?.sales_challan && setting?.enableSaleChallan && !mobile) || false) && (admin || rights?.salechallan?.view);
         const salesOrder = ((pkg?.sales_order && setting?.enableSaleOrder && !mobile) || false) && (admin || rights?.saleorder?.view);

         if (!salesInvoice && !salesReturn && !salesChallan && !salesOrder) {
            return null;
         }

         const updatedItems = route.items?.filter(sub => {
            if (sub.name === "Invoice") return salesInvoice;
            if (sub.name === "Return") return salesReturn;
            if (sub.name === "Challan") return salesChallan;
            if (sub.name === "Order") return salesOrder;
            return false;
         });
         return {
            ...route,
            items: updatedItems
         };
      }
      if (route.name === "Purchase") {
         const purchaseInvoice = (pkg?.purchase_invoice || false) && (admin || rights?.purchaseinvoice?.view);
         const purchaseReturn = (pkg?.purchase_return || false) && (admin || rights?.purchasereturn?.view);
         const purchaseChallan = ((pkg?.purchase_challan && setting?.enablePurchaseChallan && !mobile) || false) && (admin || rights?.purchasechallan?.view);
         const purchaseOrder = ((pkg?.purchase_order && setting?.enablePurchaseOrder && !mobile) || false) && (admin || rights?.purchaseorder?.view);

         if (!purchaseInvoice && !purchaseReturn && !purchaseChallan && !purchaseOrder) {
            return null;
         }

         const updatedItems = route.items?.filter(sub => {
            if (sub.name === "Invoice") return purchaseInvoice;
            if (sub.name === "Return") return purchaseReturn;
            if (sub.name === "Challan") return purchaseChallan;
            if (sub.name === "Order") return purchaseOrder;
            return false;
         });
         return {
            ...route,
            items: updatedItems
         };
      }
      if (route.name === "Inventory") {
         const issue = (pkg?.issue || false) && (admin || rights?.inventoryissue?.view);
         const receive = ((pkg?.receive && mobile) || false) && (admin || rights?.inventoryreceive?.view);
         const stockTransfer = ((pkg?.stock_transfer && mobile) || false) && (admin || rights?.inventorystocktransfer?.view);

         if (!issue && !receive && !stockTransfer) {
            return null;
         }

         const updatedItems = route.items?.filter(sub => {
            if (sub.name === "Issue") return issue;
            if (sub.name === "Receive") return receive;
            if (sub.name === "Stock Transfer") return stockTransfer;
            return false;
         });
         return {
            ...route,
            items: updatedItems
         };
      }
      if (route.name === "Cash") {
         const cash = (pkg?.cash || false) && (admin || rights?.cashPayment?.view || rights?.cashReceive?.view);

         if (!cash) {
            return null;
         }

         return route;
      }
      if (route.name === "Bank") {
         const bank = (pkg?.bank || false) && (admin || rights?.bankPayment?.view || rights?.bankReceive?.view);

         if (!bank) {
            return null;
         }

         return route;
      }
      if (route.name === "Journal Vouchers") {
         const jv = (pkg?.jv || false) && (admin || rights?.journalvouchers?.view);

         if (!jv) {
            return null;
         }

         return route;
      }
      return route;
   })
      .filter(Boolean);
};

export const mergeBarcodeSetup = (defaultConfig, newConfig) => {
   console.log("defaultConfig", defaultConfig?.isEnable, "newConfig", newConfig?.isEnable);
   const newContent = newConfig?.content ?? [];
   const defaultContent = defaultConfig.content;

   const newKeys = new Set(newContent.map(item => item.key));

   const missingDefaults = defaultContent.filter(
      item => !newKeys.has(item.key)
   );

   return {
      ...defaultConfig,
      ...newConfig,
      content: [...newContent, ...missingDefaults].sort(
         (a, b) => a.ordinal - b.ordinal
      ),
      alterations: newConfig?.alterations ?? defaultConfig.alterations,
      modifications: newConfig?.modifications ?? defaultConfig.modifications,
   };
};

export function overwriteUserRights(defaultRights, newRights) {
   const result = JSON.parse(JSON.stringify(defaultRights));

   const base = result[0];
   const incoming = newRights[0];

   Object.keys(incoming).forEach((key) => {
      if (key === 'companyId') {
         base.companyId = incoming.companyId;
         return;
      }

      if (base[key]) {
         base[key] = {
            ...base[key],
            ...incoming[key],
         };
      } else {
         base[key] = incoming[key];
      }
   });

   return result;
}

export function mergeObjects(defaultObj, newObj) {
   const result = { ...defaultObj };

   for (const key in newObj) {
      if (
         typeof newObj[key] === "object" &&
         newObj[key] !== null &&
         !Array.isArray(newObj[key]) &&
         typeof defaultObj[key] === "object"
      ) {
         result[key] = {
            ...defaultObj[key],
            ...newObj[key],
         };
      } else {
         result[key] = newObj[key];
      }
   }

   return result;
}

// export function getUniqueByStock(array) {
//    return Array.from(array.reduce((map, item) => map.set(item.serialNo, item), new Map()).values());
// }

export function getUniqueByStock(array = []) {
   return Array.from(array.reduce((map, item) => map.set(item.serialNo, item), new Map()).values());
}

export function safeDate(input) {
   const date = moment(input, [moment.ISO_8601, "DD-MM-YYYY", "DD/MM/YYYY", "YYYY-MM-DD", "YYYY/MM/DD"], true);
   if (!date.isValid()) return null; // or throw error
   return date.format("DD-MM-YYYY");
}


export function getCommonItemRate(selected = []) {
   if (!Array.isArray(selected) || selected.length === 0) return "";
   const firstRate = selected?.[0]?.itemRate;
   if (firstRate === null || firstRate === undefined || firstRate === 0) return "";
   const isSame = selected.every((x) => x?.itemRate === firstRate);
   return isSame ? firstRate : "";
}

export function mergeRecords(data = []) {
   if (!Array.isArray(data) || data.length === 0) return [];
   const result = [];
   const usedChildIds = new Set();
   for (let i = 0; i < data.length; i++) {
      let parent = { ...data[i] };
      for (let j = 0; j < data.length; j++) {
         let child = data[j];
         if (
            parent.itemId === child.itemId &&
            parent.id !== child.id &&
            (parent.pinvLineDetailId === child.id ||
               parent.pcldId === child.id)
         ) {

            parent.quantity3 =
               (parent.quantity3 || 0) + (child.quantity3 || 0);

            usedChildIds.add(child.id);
         }
      }
      result.push(parent);
   }
   return result.filter(item => !usedChildIds.has(item.id));
}

export function setLineItemData(data, itemData) {
   return data.map((item, idx) => {
      let rowIdx = idx + 1;
      const takaDetails = item?.purchaseLineDetail?.map((i) => {
         return {
            itemName: item.itemName,
            itemId: i.itemId,
            pinvLineDetailId: item.type?.toLowerCase() === "purchase" ? i.id : null,
            quantity1: i.pendingQty1,
            quantity2: i.pendingQty2,
            quantity3: i.pendingQty3,
            isSelected: true,
            id: i.id,
            issueQty3: i.issueQty3,
            purchaseQty3: i.pendingQty3,
            pcldId: item.type?.toLowerCase() === "challan" ? i.id : null,
         };
      });
      const selectedItem = itemData.find((a) => a.id === item.itemId)
      return {
         itemId: item.itemId,
         unit: item.unit ? item.unit : selectedItem?.ratepersalesid,
         qty1: takaDetails?.length > 0 ? takaDetails?.length : 0,
         qty2: 0,
         qty3: takaDetails?.length > 0 ? takaDetails?.reduce((acc, curr) => acc + curr.quantity3, 0) : 0,
         // pliId: item.id,
         pliId: item.type === "purchase" ? item.id : null,
         pcliId: item.type === "challan" ? item.id : null,
         lineNumber: rowIdx,
         isDeleted: false,
         stock: [],
         takaDetails: takaDetails
      };
   });
}

export function setIssueLineItemData(data, itemData, accountData, daybook, company) {
   return data?.map((item, idx) => {
      let row = idx + 1;
      const selectedItem = itemData.find((x) => x.id === item.itemId);
      const selectedAccount = accountData.find((x) => x.id === item.accountId);
      item.typeOfSupply = item.typeOfSupply ? item.typeOfSupply : daybook?.typeOfSupply ? daybook?.typeOfSupply : 1
      if (company?.gstRegNo && company?.setting?.applyGST && daybook?.applyGst) {
         let hsn;
         if (item?.typeOfSupply === 1) {
            if (selectedItem?.hsnData?.subHsn.length === 1) {
               hsn = selectedItem?.hsnData?.subHsn[0];
            } else {
               hsn = selectedItem?.hsnData?.subHsn.find(
                  (hsn) => item.itemRate >= hsn.fromRate && item.itemRate <= hsn.toRate,
               );
            }
         }
         if (item?.typeOfSupply === 2) {
            if (selectedItem?.sacData?.subHsn.length === 1) {
               hsn = selectedItem?.sacData?.subHsn[0];
            } else {
               hsn = selectedItem?.sacData?.subHsn.find(
                  (hsn) => item.itemRate >= hsn.fromRate && item.itemRate <= hsn.toRate,
               );
            }
         }
         const newGSt = selectedAccount?.gstRegNo ? Number(hsn?.gst) : 0;
         const mainqty = selectedItem?.primaryuqcid === selectedItem?.uqcid1 ? item.pendingQty1 : selectedItem?.primaryuqcid === selectedItem?.uqcid2 ? item.pendingQty2 : item.pendingQty3;
         const amt = Number(item.itemRate) * mainqty;
         const netGst = (amt * newGSt) / 100;
         item.amount = amt
         item.taxableAmount = amt
         item.netAmount = amt
         item.gst = newGSt
         item.netGst = netGst
         item.gstAmount = netGst + amt
         item.subHSNId = hsn?.id ?? null
      }


      const taktaDetails = item?.issueLineDetail?.map((i) => {
         return {
            ...i,
            itemName: item.itemName,
            itemId: i.itemId,
            issueLineDetailId: i.id,
            quantity1: i.pendingQty1,
            quantity2: i.pendingQty2,
            quantity3: i.pendingQty3,
            mts: "",
            remarks: "",
            id: i.id
         }
      })

      return {
         itemId: item.itemId,
         unit: item.unit ? item.unit : selectedItem?.rateperpurchaseid,
         issueLineId: item.id,
         lineNumber: row,
         gst: item.gst,
         subHSNId: item.subHSNId,
         // gstAmount: item.gstAmount,
         // amount: item.amount,
         // taxableAmount: item.taxableAmount,
         // netAmount: item.netAmount,
         isDeleted: false,
         stock: [],
         takaDetails: taktaDetails
      }
   });
}

export function removeLinkedIssueLineDetails(data) {
   const idsToRemove = new Set();

   data.forEach(a => {
      data.forEach(b => {
         if (
            a.issueLineDetailId != null &&
            a.issueLineDetailId === b.id &&
            a.id !== b.id
         ) {
            idsToRemove.add(b.id);
         }
      });
   });

   return data.filter(item => !idsToRemove.has(item.id));
}

export function removeDuplicateItdRecords(data) {
   const unique = new Map();

   data.forEach(item => {
      const key = `${item.id}_${item.issueLineDetailId}_${item.itemId}`;

      if (!unique.has(key)) {
         unique.set(key, item);
      }
   });

   return Array.from(unique.values());
}

export function mergeUsedMeter(data) {
   const pendingMap = new Map();

   data.forEach(item => {
      if (item.pendingQty3 !== undefined && item.pendingQty3 !== null) {
         pendingMap.set(`${item.itemId}_${item.id}`, item);
      }
   });

   const result = [];

   data.forEach(item => {

      if (item.usedMeter !== undefined && item.issueLineDetailId) {

         const key = `${item.itemId}_${item.issueLineDetailId}`;
         const pending = pendingMap.get(key);

         if (pending) {
            item.usedMeter =
               Number(item.usedMeter || 0) +
               Number(pending.pendingQty3 || 0);

            pendingMap.delete(key);
         }

         result.push(item);
      }

      else if (item.pendingQty3 !== undefined) {
         const key = `${item.itemId}_${item.id}`;
         if (pendingMap.has(key)) {
            result.push(item);
         }
      }

   });

   return result;
}